/**
 * Bakehunters Configurator Logic
 * Handles dynamic pricing, validation, and metaobject data.
 */

// DIAGNOSTIC: Verify script is loading
console.log('🍰 Bakehunters Configurator Script Loaded!');
console.log('   - Timestamp:', new Date().toLocaleTimeString());
console.log('   - Document state:', document.readyState);

/**
 * Icon filenames per add-on / filling handle. Module-scope because BOTH the PDP renderers and
 * addToCart need them: the cart and the Shopify order carry the resolved filename on the fee
 * line (`_Icon`), which is the only way the cart can show a real icon — the surcharge lines all
 * point at one shared "Допълнителна услуга" variant that has no image of its own.
 *
 * Not derivable from the handle: drawing-2-colors -> addon-drawing.png and
 * vanilla-cheesecake -> filling-cheesecake.png both break the pattern.
 */
/**
 * Sizes delivered free to any zone (2026-09-30, docs/business-rules.md#free-delivery-on-xl-and-xxl).
 * Mirrors QuickOrder `products.free_delivery_eligible`, which the DM channel already honours.
 * ponytail: hardcoded handles; move to a cake_size metaobject field if the list starts changing.
 */
const BH_FREE_DELIVERY_SIZES = ['circlexl', 'circlexxl', 'heartxl', 'heartxxl'];
const BH_POPULAR_SIZES = ['bentomaxi', 'heartmaxi'];

const BH_ADDON_ICONS = {
    'drawing-2-colors': 'addon-drawing.png',
    'gold-text': 'addon-gold-text.png',
    'ribbons': 'addon-ribbons.png',
    'cream-border': 'addon-cream-border.png',
    'cherries': 'addon-cherries.png',
    'pearls': 'addon-pearls.png',
    'glitter': 'addon-glitter.png',
    'side-text': 'addon-side-text.png',
};

// Money fields reach JS as { amount: "5.0" } — bakehunters-config.liquid builds the amount with
// `| plus: 0` / `| divided_by: 100.0`, and Liquid prints a float with the decimals it happens to
// have, so 5 renders "5.0" and 6 renders "6.0". Liquid has no fixed-decimal filter, so formatting
// belongs here, at display time. Same defensive shape as priceOf(): object, bare string, or null.
const bhMoney = (v) => (parseFloat(v && v.amount != null ? v.amount : v) || 0).toFixed(2);

const BH_FILLING_ICONS = {
    'cherry': 'filling-cherry.png',
    'strawberry': 'filling-strawberry.png',
    'raspberry': 'filling-raspberry.png',
    'chocolate-cream': 'filling-chocolate-cream.png',
    'vanilla-cheesecake': 'filling-cheesecake.png',
    // Keyed on the METAOBJECT handle, which still reads "curd" — only the customer-facing
    // name became "Лимонов крем". Renaming the handle would break filling_code and the fee
    // mapping for no gain.
    'lemon-curd-blueberry': 'filling-lemon-cream.png',
};

class BakehuntersConfigurator {
    constructor() {
        console.log('🔧 Bakehunters: Constructor called');

        this.config = window.BakehuntersConfig || {};
        console.log('   - Config loaded:', !!this.config, '| Sizes:', this.config.sizes?.length || 0);

        // Reorder colour swatches by visual similarity so they're easier to scan than the
        // arbitrary metaobject order (client request). Runs once; renderColors + the
        // firstLight fallback all read this.config.colors after sorting.
        this.sortColorsBySimilarity();

        // CRITICAL: Store the original valid variant ID from HTML before anything else
        const variantInput = document.getElementById('VariantIdInput');
        this.originalVariantId = variantInput ? variantInput.value : null;

        // Log for debugging
        console.log('   - Original variant ID from HTML:', this.originalVariantId);
        console.log('   - SizePills element exists:', !!document.getElementById('SizePills'));
        console.log('   - BakehuntersForm exists:', !!document.getElementById('BakehuntersForm'));

        this.variantData = this.loadVariantData();

        // Initialize Base Price - Force to 0 initially, will be calculated by calculateTotal() based on defaults
        // This avoids the "34 vs 29" issue where it might pick up a wrong variant price
        let initialBasePrice = 0;

        // State
        this.state = {
            shape: null,          // 'round' or 'heart'
            sizeHandle: null,
            fillings: [],
            sponge: null,
            colorHandle: null,
            message: '',
            deliveryMethod: null,
            deliveryZone: null,
            date: null,
            timeSlot: null,
            rushFee: 0,
            basePrice: initialBasePrice, // Set Verified Start Price
            totalPrice: initialBasePrice,
            selectedVariantId: this.originalVariantId // Track the currently selected variant
        };

        // Check for URL parameters for pre-fill
        this.parseUrlParameters();

        this.init();
    }

    // Hex "#rrggbb"/"#rgb" -> {h (0-360), s (0-1), l (0-1)}. Shared by the swatch sort
    // and the staining check.
    hexToHsl(hex) {
        hex = String(hex || '').replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        const r = parseInt(hex.slice(0, 2), 16) / 255,
              g = parseInt(hex.slice(2, 4), 16) / 255,
              b = parseInt(hex.slice(4, 6), 16) / 255;
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
        let h = 0, s = 0;
        if (mx !== mn) {
            const d = mx - mn;
            s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
            if (mx === r) h = (g - b) / d + (g < b ? 6 : 0);
            else if (mx === g) h = (b - r) / d + 2;
            else h = (r - g) / d + 4;
            h *= 60;
        }
        return { h, s, l };
    }

    // Deep-pigment colours tint teeth/tongue: dark shades, plus any reasonably saturated
    // non-light colour (black, navy, red, bordo, brown, orange, green, blue, purple). The
    // l<0.58 gate keeps pastels + yellow + pink out, so the badge only shows when it matters.
    colorStainsHeavily(color) {
        if (!color || !color.hex) return false;
        const { s, l } = this.hexToHsl(color.hex);
        return l < 0.40 || (s > 0.40 && l < 0.58);
    }

    // Group colour swatches by similarity: neutrals (white/cream/black) first, then the
    // chromatic colours walked around the wheel. Near-black/near-white read as achromatic
    // (their computed saturation is unreliable), so neutral = low saturation OR extreme
    // lightness. The hue seam is rotated +120° so it falls in the palette's navy→purple gap,
    // keeping pinks+reds (and blues) contiguous instead of split across the 0°/360° boundary.
    sortColorsBySimilarity() {
        const colors = this.config.colors;
        if (!Array.isArray(colors) || colors.length < 3) return;
        const key = (c) => { const k = this.hexToHsl(c.hex); k.neutral = k.s < 0.15 || k.l > 0.90 || k.l < 0.12; return k; };
        colors.sort((a, b) => {
            const A = key(a), B = key(b);
            if (A.neutral !== B.neutral) return A.neutral ? -1 : 1;   // neutrals first
            if (A.neutral) return B.l - A.l;                          // light -> dark
            const ah = (A.h + 120) % 360, bh = (B.h + 120) % 360;     // seam in navy->purple gap
            if (Math.abs(ah - bh) > 1) return ah - bh;                // walk the wheel
            return B.l - A.l;                                         // tiebreak: lighter first
        });
    }

    loadVariantData() {
        const script = document.querySelector('script[id^="Bakehunters-VariantData-"]');
        if (script) {
            try {
                const data = JSON.parse(script.textContent);
                this.productVariants = data.variants || [];
                this.firstVariantId = data.firstVariantId;
                console.log('Bakehunters: Loaded', this.productVariants.length, 'variants. First:', this.firstVariantId);
                return data;
            } catch (e) {
                console.error('Failed to parse variant data:', e);
            }
        }
        return { variants: [], firstVariantId: null };
    }

    // ---- i18n helpers ----
    // Look up an injected UI string (window.BakehuntersConfig.t); falls back to the key.
    t(key) {
        return (this.config.t && this.config.t[key] != null) ? this.config.t[key] : key;
    }

    // Pick the locale-appropriate display name from a { nameBG, nameEN } object.
    localeName(obj) {
        if (!obj) return '';
        const isBG = String(this.config.locale || 'bg').toLowerCase().startsWith('bg');
        return isBG ? (obj.nameBG || obj.nameEN || '') : (obj.nameEN || obj.nameBG || '');
    }

    // Short size label for the size cards: the shape is already chosen above, so
    // "Bento Heart"/"Heart Maxi"/"Round L" would repeat it. Both shapes collapse to
    // the same tier labels (Bento / Maxi / L / XL / XXL). The cart "Size" property
    // keeps the full descriptive name (see updateSizeProperty).
    sizeLabel(size) {
        const isBG = String(this.config.locale || 'bg').toLowerCase().startsWith('bg');
        const MAP = {
            bentoclassic: ['Bento', 'Бенто'], bentoheart: ['Bento', 'Бенто'],
            bentomaxi: ['Maxi', 'Макси'], heartmaxi: ['Maxi', 'Макси'],
            circlel: ['L', 'L'], heartl: ['L', 'L'],
            circlexl: ['XL', 'XL'], heartxl: ['XL', 'XL'],
            circlexxl: ['XXL', 'XXL'], heartxxl: ['XXL', 'XXL'],
        };
        const m = MAP[size.handle];
        return m ? m[isBG ? 1 : 0] : (this.localeName(size) || size.name);
    }

    init() {
        this.cacheDOMElements();
        this.bindEvents();
        this.addFormValidation();
        // Editing a cake from the cart (?bh_edit=<_Cake_ID>, see assets/bh-cart-edit.js): the
        // saved state REPLACES the preset seed rather than merging with it. Merging would
        // re-tick every preset add-on the customer had deliberately unticked.
        this.edit = window.BhCartEdit ? window.BhCartEdit.context(this.config) : null;
        if (this.edit) {
            Object.assign(this.state, this.edit.saved.state || {});
        } else {
            this.applyUrlPrefill();
            this.applyPresetSeed();
        }
        this.renderInitialState();
        this.initDatePicker();
        if (this.edit) window.BhCartEdit.hydrate(this, this.edit.saved);
    }

    // Seed the configurator from this design's metafields (preset-cake PDPs).
    // Pre-checks the design's decorations + selects its shape/color/message, all
    // editable. Only fills state that URL params (personalize handoff) didn't already set.
    applyPresetSeed() {
        const p = this.config.preset;
        if (!p) return;
        // PRODUCT DECISION — DO NOT REMOVE (this has been ripped out twice):
        // every preset design MUST open fully pre-configured to match its photo — shape,
        // colour, decorations and suggested message all pre-selected, plus a default size so
        // a concrete START PRICE shows instead of a confusing "€30 – €79" range. This is the
        // whole model: the customer is buying "this design as pictured". Nothing is hidden —
        // the shown price includes every pre-checked add-on, and the customer can uncheck any
        // of them to drop the price live. All seeds are editable; URL params (personalize
        // handoff) still win over them.
        if (!this.state.shape && p.shape) this.state.shape = p.shape;

        // Default size = cheapest size of the chosen shape, so the price is a real number
        // ("start price") the moment the design opens. Customer can size up freely.
        if (!this.state.sizeHandle && this.state.shape) {
            const priceOf = (s) => parseFloat((s.basePrice && s.basePrice.amount) || s.basePrice || 0);
            const forShape = (this.config.sizes || []).filter(s => s.shape === this.state.shape);
            if (forShape.length) {
                this.state.sizeHandle = forShape.reduce((a, b) => (priceOf(a) <= priceOf(b) ? a : b)).handle;
            }
        }

        if (!this.state.colorHandle && p.color &&
            (this.config.colors || []).some(c => c.handle === p.color)) {
            this.state.colorHandle = p.color;
        }
        if (!this.state.message && p.message) this.state.message = p.message;
        if (Array.isArray(p.addons) && p.addons.length) {
            this.state.addons = this.state.addons || {};
            const valid = new Set((this.config.addons || []).map(a => a.handle));
            p.addons.forEach(h => {
                if (valid.has(h) && !(h in this.state.addons)) this.state.addons[h] = true;
            });
        }
        console.log('Bakehunters: applied preset seed', p);
    }

    applyUrlPrefill() {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('personalize_from')) return;
        const map = {
            size: 'sizeHandle',
            shape: 'shape',
            sponge: 'sponge',
            color: 'color',
            message: 'message',
            date: 'date',
            method: 'method'
        };
        Object.entries(map).forEach(([param, stateKey]) => {
            if (params.has(param)) this.state[stateKey] = params.get(param);
        });
        if (params.has('filling')) {
            // An old link or a preset may carry a combination we no longer sell.
            const seeded = params.get('filling').split(',').filter(Boolean);
            this.state.fillings = window.BhFillingRules ? BhFillingRules.clean(seeded) : seeded;
        }
        console.log(`Bakehunters: pre-filled from preset cake "${params.get('personalize_from')}"`);
    }

    initDatePicker() {
        if (!this.dom.deliveryDate) return;

        // Set minimum date
        const today = new Date();
        this.dom.deliveryDate.min = today.toISOString().split('T')[0];

        // Add change listener for blocked days
        this.dom.deliveryDate.addEventListener('change', (e) => {
            if (!e.target.value) {
                this.state.date = null;
                return;
            }

            const selectedDate = new Date(e.target.value);
            const dayOfWeek = selectedDate.getDay();
            // FIX: Parse blockedWeekdays if it's a string
            let blockedDays = this.config.shop?.blockedWeekdays || [1, 2];
            if (typeof blockedDays === 'string') {
                try { blockedDays = JSON.parse(blockedDays); } catch (e) { blockedDays = [1, 2]; }
            }

            if (Array.isArray(blockedDays) && blockedDays.includes(dayOfWeek)) {
                // Backstop only: <bh-date-picker> no longer writes closed weekdays into this
                // input (tapping Mon/Tue opens the fridge-freshness sheet instead). If some
                // other path still sets one, route it to that same sheet rather than a raw
                // browser alert() — alerts also freeze the Chrome automation session.
                const rejected = e.target.value;
                e.target.value = '';
                this.state.date = null;
                const picker = document.querySelector('bh-date-picker');
                if (picker && picker.showClosedDayInfo) picker.showClosedDayInfo(rejected);
                return;
            }

            this.state.date = e.target.value;
            this.checkRushAndAvailability();
            this.updateTimeSlots();
            this.updatePickupHours();
        });
    }

    cacheDOMElements() {
        this.dom = {
            form: document.getElementById('BakehuntersForm'),
            variantInput: document.getElementById('VariantIdInput'),
            shapePills: document.getElementById('ShapePills'),
            sizePills: document.getElementById('SizePills'),
            fillingOptions: document.getElementById('FillingOptions'),
            colorSwatches: document.getElementById('ColorSwatches'),
            cakeMessage: document.getElementById('CakeMessage'),
            deliveryDate: document.getElementById('DeliveryDate'),
            datePicker: document.querySelector('bh-date-picker'),
            timeSlotSelect: document.getElementById('TimeSlotSelect'),
            deliveryZoneWrapper: document.getElementById('DeliveryZoneWrapper'),
            deliveryZoneSelect: document.getElementById('DeliveryZoneSelect'),
            priceDisplay: document.getElementById('FinalPrice'),
            rushFeeNotice: document.getElementById('RushFeeNotice'),
            rushExplainer: document.getElementById('RushExplainer'),
            noRushNote: document.getElementById('NoRushNote'),
            rushFeeAmount: document.getElementById('RushFeeAmount'),
            rushFeeAmount: document.getElementById('RushFeeAmount'),
            pickupRadios: document.querySelectorAll('input[name="properties[Delivery Method]"]'),
            deliveryPills: document.getElementById('DeliveryMethodPills'),
            qtyMinus: document.getElementById('QtyMinus'),
            qtyPlus: document.getElementById('QtyPlus'),
            qtyInput: document.getElementById('QuantityInput'),
            charCount: document.getElementById('ResultCharCount'),
            maxChar: document.getElementById('MaxCharLimit'),
            mainPriceDisplay: document.getElementById('DisplayPrice')
        };

        console.log('Bakehunters: DOM elements cached');
        console.log('  - Delivery zone wrapper:', !!this.dom.deliveryZoneWrapper);
        console.log('  - Delivery zone select:', !!this.dom.deliveryZoneSelect);
        console.log('  - Pickup radios found:', this.dom.pickupRadios.length);
    }

    bindEvents() {
        // Delivery Method Pills
        if (this.dom.deliveryPills) {
            console.log('Bakehunters: Binding delivery method pills.');
            this.dom.deliveryPills.addEventListener('click', (e) => {
                const pill = e.target.closest('.bh-delivery-pill');
                if (!pill) return;

                const method = pill.dataset.method;
                console.log('Bakehunters: Delivery method changed to:', method);

                // Update State
                this.state.deliveryMethod = method;

                // Update Visuals + radio semantics
                this.dom.deliveryPills.querySelectorAll('.bh-delivery-pill').forEach(p => {
                    p.classList.remove('active');
                    p.setAttribute('aria-checked', 'false');
                });
                pill.classList.add('active');
                pill.setAttribute('aria-checked', 'true');

                // Update Hidden Inputs
                const radio = this.dom.form.querySelector(`input[name="properties[Delivery Method]"][value="${method}"]`);
                if (radio) radio.checked = true;

                this.updateDeliveryUI();
                this.calculateTotal();
            });
        }

        // Quantity Selectors
        if (this.dom.qtyMinus && this.dom.qtyPlus && this.dom.qtyInput) {
            this.dom.qtyMinus.addEventListener('click', () => {
                let current = parseInt(this.dom.qtyInput.value) || 1;
                if (current > 1) {
                    this.dom.qtyInput.value = current - 1;
                    this.calculateTotal(); // Technically total doesn't change per unit but good practice
                }
            });

            this.dom.qtyPlus.addEventListener('click', () => {
                let current = parseInt(this.dom.qtyInput.value) || 1;
                if (current < 10) {
                    this.dom.qtyInput.value = current + 1;
                    this.calculateTotal();
                }
            });
        }

        // Fallback: Bind radios just in case (though hidden now)
        this.dom.pickupRadios.forEach((radio) => {
            radio.addEventListener('change', (e) => {
                // ... legacy handler if needed ...
            });
        });

        // Date Picker
        // Date Picker (Moved to initDatePicker)
        // Removed duplicate listener

        // Zone Select
        if (this.dom.deliveryZoneSelect) {
            this.dom.deliveryZoneSelect.addEventListener('change', (e) => {
                this.state.deliveryZone = e.target.value;
                this.updateTimeSlots();
                this.calculateTotal();
            });
        }

        // Message
        if (this.dom.cakeMessage) {
            this.dom.cakeMessage.addEventListener('input', (e) => {
                this.state.message = e.target.value;
                const len = e.target.value.length;
                if (this.dom.charCount) this.dom.charCount.textContent = len;

                // Color-coded feedback on the counter chip
                const wrapper = document.getElementById('CharCountWrapper');
                if (wrapper) {
                    const max = parseInt(this.dom.maxChar?.textContent || '30', 10);
                    wrapper.classList.toggle('bh-char-count--warning', len >= max - 5 && len < max);
                    wrapper.classList.toggle('bh-char-count--limit', len >= max);
                }
            });
        }
    }

    addFormValidation() {
        if (!this.dom.form) return;
        this.dom.form.addEventListener('submit', this.handleFormSubmit.bind(this));

        const atcBtn = this.dom.form.querySelector('.bh-atc-btn');
        if (atcBtn) {
            atcBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.dom.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            });
        }
    }

    async handleFormSubmit(e) {
        e.preventDefault();

        // ---- Validation gate: require the essential choices before adding to cart ----
        const missing = this.validateConfiguration();
        if (missing) {
            this.flagMissing(missing);
            return;
        }
        const _notice = document.getElementById('ValidationNotice');
        if (_notice) _notice.classList.add('bh-is-hidden');

        // The address decides the neighbourhood (assets/bh-address.js). If its last check had not
        // run yet (field still focused) and it switches the neighbourhood now, stop: the fee and
        // the slot just changed and the customer has to see that before the cake goes in.
        if (window.bhAddress && (await window.bhAddress.settle())) return;

        // Notes ask for a paid decoration that isn't ticked? bh-addon-check.js asks the customer
        // in a sheet; false = they are still deciding, so stop here. Fails open: no answer in
        // time and the cake goes in as usual.
        if (window.bhAddonCheck && !(await window.bhAddonCheck.gate(this))) return;

        console.log('========================================');
        console.log('🍰 BAKEHUNTERS DEBUG: Form Submit Started');
        console.log('========================================');

        if (this.isSubmitting) {
            console.log('❌ STEP 0: Already submitting, blocked.');
            return;
        }
        this.isSubmitting = true;

        const btn = this.dom.form.querySelector('.bh-atc-btn');
        const originalText = btn?.innerHTML || this.t('add_to_cart');
        if (btn) {
            btn.innerHTML = this.t('checking');
            btn.style.opacity = '0.7';
        }

        try {
            // ==================== STEP 0: RUSH FEE CHECK ====================
            // Rush Fee Variant ID from config
            const RUSH_FEE_VARIANT_ID = this.config.shop?.fees?.rush || "48234857693452";
            const items = [];
            const needsRushFee = this.state.rushFee > 0;

            if (needsRushFee) {
                console.log(`⚠️ Rush fee applies: ${this.state.rushFee} EUR. Bundle add required.`);

                // Validate rush fee variant ID exists and is valid
                if (!RUSH_FEE_VARIANT_ID || RUSH_FEE_VARIANT_ID === "48234857693452") {
                    console.warn('⚠️ Using default rush fee variant ID. Configure window.BakehuntersConfig.rushFeeVariantId if needed.');
                }
            }

            // ==================== STEP 1: VERIFY VARIANT ID ====================
            console.log('📋 STEP 1: Using selected variant ID from state');

            // Use the variant ID that was selected when the user chose a size
            const variantIdToAdd = this.state.selectedVariantId || this.originalVariantId;

            console.log('  - Selected Variant ID:', variantIdToAdd);
            console.log('  - Selected Size:', this.state.sizeHandle);

            if (!variantIdToAdd) {
                throw new Error('Could not determine product variant. Please refresh the page.');
            }

            console.log('✅ STEP 1 COMPLETE: Using variant ID:', variantIdToAdd);

            // ==================== STEP 2: Verify Form Input ====================
            console.log('📋 STEP 2: Verifying form input matches selected variant');
            if (this.dom.variantInput) {
                this.dom.variantInput.value = variantIdToAdd;
                console.log('  - Form variant input:', this.dom.variantInput.value);
            }

            // ==================== STEP 3: Validate Date ====================
            console.log('📋 STEP 3: Validating Date');
            const formData = new FormData(this.dom.form);
            const date = formData.get('properties[Delivery Date]');
            console.log('  - Delivery Date:', date);
            if (!date) {
                alert(this.t('select_date'));
                throw new Error('Validation failed');
            }

            // ==================== STEP 4: Build Payload ====================
            if (btn) btn.innerHTML = this.t('adding');

            console.log('📋 STEP 4: Building Request Payload');

            // One token per configured cake, stamped on the cake line AND every surcharge line
            // it generates. Before this, add-ons were tied to their cake by `_Related_To: "Cake
            // for <date>"` — the DATE ONLY. Two cakes for the same day produced add-ons nobody
            // could attribute: gold text on one and a drawing on the other, with both lines
            // claiming "Cake for 2026-08-30". For a bakery that physically decorates each cake,
            // that is an order you can make wrong.
            //
            // Side effect, and a deliberate one: because the token differs per add-to-cart, two
            // IDENTICAL cakes no longer merge into one line at quantity 2. They stay separate,
            // which is what you want when each can carry its own add-ons.
            const cakeToken = this.newCakeToken();
            console.log('  - Cake token:', cakeToken);

            // Collect Form Data Properties
            const properties = {};
            for (const [key, value] of formData.entries()) {
                if (!key.startsWith('properties[')) continue;
                // A file input yields a File, not a string — and an EMPTY file input still
                // yields a zero-byte File, so this guard has to run whether or not a photo
                // was picked. Files can't go through the JSON items API at all; they ride
                // the multipart path in addCakeWithFile().
                if (typeof value !== 'string') continue;
                const cleanKey = key.replace('properties[', '').replace(']', '');
                if (value.trim() !== '') {
                    const [propName, propValue] = this.orderProperty(cleanKey, value, this.state.deliveryMethod);
                    properties[propName] = propValue;
                }
            }

            // ==================== COLLECT ALL FEE VARIANTS ====================
            console.log('📋 STEP 4A: Collecting Fee Variants');

            // Helper function to convert GID to numeric ID
            const extractNumericId = (id) => {
                if (!id) return null;
                // If it's already numeric, return as-is
                if (typeof id === 'number' || /^\d+$/.test(id)) return id.toString();
                // If it's a GID, extract the numeric part
                if (id.includes('gid://shopify/ProductVariant/')) {
                    return id.split('/').pop();
                }
                return id;
            };

            // 1. Rush Fee
            if (needsRushFee) {
                const numericRushId = extractNumericId(RUSH_FEE_VARIANT_ID);
                console.log(`⚠️ Adding Rush Fee with variant ID: ${numericRushId} (original: ${RUSH_FEE_VARIANT_ID})`);
                if (numericRushId) {
                    items.push({
                        id: numericRushId,
                        quantity: 1,
                        properties: {
                            "_Cake_ID": cakeToken,
                            "_Related_To": this.cakeLabel(cakeToken, date),
                            "_Type": "Rush Order Surcharge",
                            // The one visible property — Shopify's checkout and the admin
                            // order render every property whose key does not start with "_".
                            // The KEY names the kind of surcharge (Такса / Доставка / Пълнеж
                            // / Добавка) because one blanket word does not fit all four: a
                            // decoration is a добавка, a delivery charge is not. This is what
                            // identifies the line, since the shared fee VARIANT cannot — the
                            // €3 variant is titled "Ванилов чийз крем" and is ALSO every €3
                            // decoration add-on.
                            [this.t('fee_key_rush')]: this.t('fee_val_rush')
                        }
                    });
                }
            }

            // 2. Delivery Zone Fee
            // XL / XXL deliver free: no fee line at all (the zone still rides on the cake line).
            if (this.state.deliveryMethod === 'Delivery' && this.state.deliveryZone && !this._freeDelivery()) {
                const zoneVariantId = this.config.shop?.fees?.zones?.[this.state.deliveryZone];
                const numericZoneId = extractNumericId(zoneVariantId);
                if (numericZoneId && !zoneVariantId.includes('UNKNOWN')) {
                    console.log(`📦 Adding Delivery Zone Fee: ${this.state.deliveryZone} -> ${numericZoneId} (original: ${zoneVariantId})`);
                    const zoneData = this.config.zones?.find(z => z.handle === this.state.deliveryZone);
                    items.push({
                        id: numericZoneId,
                        quantity: 1,
                        properties: {
                            "_Cake_ID": cakeToken,
                            "_Related_To": this.cakeLabel(cakeToken, date),
                            "_Type": "Delivery Zone Fee",
                            "_Zone": this.state.deliveryZone,
                            [this.t('fee_key_delivery')]: zoneData ? this.localeName(zoneData) : this.state.deliveryZone
                        }
                    });
                } else {
                    console.warn('⚠️ No variant mapping found for delivery zone:', this.state.deliveryZone);
                }
            }

            // 3. Premium Filling Fees
            if (this.state.fillings && this.state.fillings.length > 0) {
                // Determine Tier for Cart Properties
                const sizeData = this.config.sizes.find(s => s.handle === this.state.sizeHandle);
                const isLarge = sizeData && (
                    sizeData.tier === 'large' ||
                    ['l', 'xl', 'xxl', 'heart-l', 'heart-xl', 'heart-xxl', 'circlel', 'circlexl', 'circlexxl', 'heartl', 'heartxl', 'heartxxl'].includes(sizeData.code?.toLowerCase()) ||
                    (sizeData.portions && parseInt(sizeData.portions.split('-')[0]) >= 10)
                );

                this.state.fillings.forEach(fillingHandle => {
                    let fillingVariantId = this.config.shop?.fees?.fillings?.[fillingHandle];

                    // Handle Tiered Object Logic ({ standard: "ID", large: "ID" })
                    if (fillingVariantId && typeof fillingVariantId === 'object') {
                        fillingVariantId = isLarge ? fillingVariantId.large : fillingVariantId.standard;
                    }

                    const numericFillingId = extractNumericId(fillingVariantId);
                    const filling = this.config.fillings.find(f => f.handle === fillingHandle);

                    if (filling && filling.isPremium && numericFillingId) {
                        const feeAmount = isLarge ?
                            (filling.premiumFeeLarge?.amount || filling.premiumFeeLarge || 0) :
                            (filling.premiumFee?.amount || filling.premiumFee || 0);

                        console.log(`🍰 Adding Premium Filling Fee: ${fillingHandle} -> ${numericFillingId} (Tier: ${isLarge ? 'Large' : 'Standard'}, Fee: ${feeAmount})`);
                        items.push({
                            id: numericFillingId,
                            quantity: 1,
                            properties: {
                                "_Cake_ID": cakeToken,
                            "_Related_To": this.cakeLabel(cakeToken, date),
                                "_Type": "Premium Filling Fee",
                                "_Filling": filling.nameEN,
                                "_Icon": BH_FILLING_ICONS[fillingHandle] || '',
                                "_Tier": isLarge ? "Large" : "Standard",
                                "_Fee": feeAmount + " EUR",
                                [this.t('fee_key_filling')]: this.localeName(filling)
                            }
                        });
                    }
                });
            }

            // 4. Dark Color Surcharge — REMOVED
            // Dark colors are now gated behind the "dark-colors" add-on.
            // The fee is handled as part of the add-on (€3 standard / €5 large).
            // No separate cart line item is needed.

            // 5. Add-ons
            if (this.state.addons) {
                Object.keys(this.state.addons).forEach(handle => {
                    const addon = this.config.addons.find(a => a.handle === handle);
                    if (addon) {
                        // Determine Price Variant
                        // Determine Tier for Cart Properties (Recalculate or use captured)
                        const sizeData = this.config.sizes.find(s => s.handle === this.state.sizeHandle);
                        const isLarge = sizeData && (
                            sizeData.tier === 'large' ||
                            ['l', 'xl', 'xxl', 'heart-l', 'heart-xl', 'heart-xxl', 'circlel', 'circlexl', 'circlexxl', 'heartl', 'heartxl', 'heartxxl'].includes(sizeData.code?.toLowerCase()) ||
                            (sizeData.portions && parseInt(sizeData.portions.split('-')[0]) >= 10)
                        );

                        const price = isLarge ? addon.priceLarge : addon.priceStandard;
                        // Construct config key e.g. "large-7.50" or "standard-5"
                        // Handle "standard-10" or "large-15.00"
                        // Mapping logic:
                        // standard-3, standard-5, standard-7, standard-10
                        // large-4.50, large-7.50, large-10.50, large-15.00
                        const tierKey = isLarge ? 'large' : 'standard';
                        // Format price to match config keys (e.g. 7.50, 4.50, 5, 3)
                        // If logic is overly complex, we can use the specific mapping

                        let mappingKey = `${tierKey}-${price}`;
                        // Fix for integer prices: 5 -> 5, 5.00 -> 5 in keys? no, check config keys
                        // Config keys: "standard-3", "standard-5", "large-4.50"
                        if (Number.isInteger(price)) mappingKey = `${tierKey}-${price}`;
                        else mappingKey = `${tierKey}-${price.toFixed(2)}`;

                        let addonVariantId = this.config.shop?.fees?.addons?.[mappingKey];

                        // Fallback manual checks if direct key fails (e.g. format mismatch)
                        if (!addonVariantId) {
                            // Try specific handle aliases first
                            addonVariantId = this.config.shop?.fees?.addons?.[handle];
                            if (addonVariantId && typeof addonVariantId === 'object') {
                                // Shouldn't happen based on config structure but safe check
                                addonVariantId = isLarge ? addonVariantId.large : addonVariantId.standard;
                            }
                        }

                        const numericAddonId = extractNumericId(addonVariantId);

                        if (numericAddonId) {
                            const val = this.state.addons[handle];
                            const props = {
                                "_Cake_ID": cakeToken,
                            "_Related_To": this.cakeLabel(cakeToken, date),
                                "_Type": "Unbundled Add-on",
                                "_Addon": addon.nameEN,
                                "_Icon": BH_ADDON_ICONS[handle] || '',
                                "_Tier": isLarge ? "Large" : "Standard",
                                "_Fee": price + " EUR",
                                [this.t('fee_key_addon')]: this.localeName(addon)
                            };
                            if (val !== true && typeof val === 'string') {
                                props["Custom Text"] = val;
                            }

                            items.push({
                                id: numericAddonId,
                                quantity: 1,
                                properties: props
                            });
                        } else {
                            console.warn(`⚠️ No variant found for addon ${handle} (${mappingKey})`);
                        }
                    }
                });
            }

            console.log(`✅ Total items to add: ${items.length} (1 cake + ${items.length - 1} fees)`);

            // Get quantity from input (cakes only - fees are per order, not per cake)
            const cakeQuantity = parseInt(this.dom.qtyInput?.value) || 1;
            console.log(`🍰 Quantity selected: ${cakeQuantity}`);

            // Construct Items Array (Main Item)
            // Note: We use unshift to put the main cake FIRST in the cart
            const numericMainId = extractNumericId(variantIdToAdd);
            console.log(`🍰 Main cake variant: ${numericMainId} (original: ${variantIdToAdd})`);
            // An edited cake keeps its reference photo unless a new one was picked: a file
            // input cannot be pre-filled, so the uploaded file's URL is carried over instead.
            const pickedFile = this.dom.form.querySelector('input[type="file"]')?.files?.[0];
            if (this.edit && this.edit.photo && !pickedFile) {
                properties[this.edit.photo[0]] = this.edit.photo[1];
            }
            items.unshift({
                id: numericMainId,
                quantity: cakeQuantity,
                properties: {
                    ...properties,
                    '_Cake_ID': cakeToken,
                    // CRO-001 (docs/experiments.md): set by bh-size-upsell.js when the upgrade
                    // offer was accepted, dropped if they switched size again afterwards.
                    ...(this.state.upsell && this.state.upsell.to === this.state.sizeHandle
                        ? { '_bh_upsell': this.state.upsell.label } : {}),
                    // Hidden snapshot that lets the cart's "Edit" button rebuild this cake.
                    ...(window.BhCartEdit ? { '_bh_state': window.BhCartEdit.snapshot(this) } : {})
                }
            });

            // Filter out items with UNKNOWN or invalid IDs
            const validItems = items.filter(item => {
                const isValidId = item.id && !String(item.id).includes('UNKNOWN');
                if (!isValidId) {
                    console.warn('⚠️ Skipping invalid item (probably missing fee mapping):', item);
                }
                return isValidId;
            });

            // ==================== STEP 5: Send Request ====================
            console.log('📋 STEP 5: Sending AJAX Request (Bundle)');
            console.log('  - URL:', window.Shopify.routes.root + 'cart/add.js');
            console.log('  - Items:', validItems);

            // A reference photo can only travel as multipart form-data — the JSON items API
            // has no way to carry a file. So when (and only when) one is attached, the cake
            // goes up on its own as FormData first, then the fees follow as the usual JSON
            // batch. With no photo the request is byte-for-byte the original single call.
            const fileInput = this.dom.form.querySelector('input[type="file"]');
            const attachedFile = fileInput && fileInput.files && fileInput.files[0];

            let response;
            if (attachedFile) {
                console.log(`  - Reference photo attached (${attachedFile.name}) → multipart path`);
                response = await this.addCakeWithFile(validItems);
            } else {
                response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ items: validItems })
                });
            }

            console.log('📋 STEP 6: Response Received');
            console.log('  - response.ok:', response.ok);
            console.log('  - response.status:', response.status);

            const data = await response.json();
            console.log('  - response.json():', data);

            // ==================== STEP 7: Check Response ====================
            if (!response.ok) {
                console.error('❌ Request failed!');
                console.error('  - Data:', data);
                throw new Error(data.description || data.message || 'Failed to add to cart');
            }

            // ==================== STEP 8: Success ====================
            console.log('✅ SUCCESS! Items added to cart');

            // Check if rush fee failed (e.g., if placeholder ID is invalid)
            // Usually, cart/add.js will add valid items and return errors for invalid ones in specific formats, 
            // but standard behavior is all-or-nothing or partial success depending on API version.
            // We assume success if status is 200.

            // Update cart count
            try {
                const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
                const cartData = await cartRes.json();
                console.log('  - Cart count:', cartData.item_count);
                const countElements = document.querySelectorAll('.cart-count-bubble, #CartCount, .cart-count');
                countElements.forEach(el => el.textContent = cartData.item_count);

                // Durable record of what THIS cake required. Line items can vanish; cart
                // attributes survive to the order. The rush fee is the one that matters —
                // a same-day cake whose €5 line is missing needs a phone call before it's
                // baked. Appended, never overwritten, so a second cake keeps its own line.
                await this.recordRequiredSurcharges(cartData, date, needsRushFee);

                // Trigger theme specific drawer if available
                if (window.theme && window.theme.cart && typeof window.theme.cart.update === 'function') {
                    // Some themes have global update methods
                }
                // If Pinnacle theme uses a specific event:
                document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', {
                    bubbles: true
                }));

            } catch (err) {
                console.warn('Failed to update cart count visual', err);
            }

            // Edit = replace. The old cake is removed only AFTER the new one is safely in the
            // cart, so a failure leaves two cakes (one deletable) rather than none.
            if (this.edit) {
                try { await window.BhCartEdit.removeCake(this.edit.cakeId); } catch (e) { console.warn('Old cake not removed', e); }
                window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
                return;
            }

            console.log('🎉 Item added - attempting to open cart drawer or redirect');

            // Try multiple methods to open cart drawer (theme-specific)
            let drawerOpened = false;

            // Method 1: HTML dialog element (modern themes)
            const drawerDialog = document.getElementById('Cart-Drawer');
            if (drawerDialog && typeof drawerDialog.showModal === 'function') {
                try {
                    drawerDialog.showModal();
                    drawerOpened = true;
                    console.log('✅ Opened cart via <dialog> element');
                } catch (e) {
                    console.warn('Failed to open dialog:', e);
                }
            }

            // Method 2: Theme-specific cart drawer toggle (Pinnacle/Dawn themes)
            if (!drawerOpened && typeof window.theme?.openCartDrawer === 'function') {
                try {
                    window.theme.openCartDrawer();
                    drawerOpened = true;
                    console.log('✅ Opened cart via theme.openCartDrawer()');
                } catch (e) {
                    console.warn('Failed to open theme drawer:', e);
                }
            }

            // Method 3: Click cart icon to trigger drawer
            if (!drawerOpened) {
                const cartIcon = document.querySelector('[data-cart-drawer], .cart-icon, #cart-icon-bubble');
                if (cartIcon) {
                    try {
                        cartIcon.click();
                        drawerOpened = true;
                        console.log('✅ Opened cart via cart icon click');
                    } catch (e) {
                        console.warn('Failed to click cart icon:', e);
                    }
                }
            }

            // Fallback: Redirect to cart page
            if (!drawerOpened) {
                console.log('ℹ️ No drawer found, redirecting to /cart');
                window.location.href = '/cart';
            }

        } catch (error) {
            console.error('========================================');
            console.error('❌ BAKEHUNTERS DEBUG: Error Caught');
            console.error('  - Error message:', error.message);
            console.error('========================================');

            if (error.message !== 'Validation failed') {
                alert(this.t('cart_error') + error.message);
            }
        } finally {
            this.isSubmitting = false;
            if (btn) {
                btn.innerHTML = originalText;
                btn.style.opacity = '1';
            }
            console.log('🏁 BAKEHUNTERS DEBUG: Submit handler finished');
        }
    }

    /**
     * Short, unique id for one configured cake. Stamped on the cake line and every surcharge
     * line it creates, so an add-on can always be traced back to the exact cake it belongs to.
     *
     * Base36 timestamp + randomness: short enough to read aloud in the kitchen, and unique
     * across two cakes configured seconds apart in the same session, which a timestamp alone
     * would not guarantee.
     *
     * @returns {string} e.g. "k3f9a2"
     */
    newCakeToken() {
        const t = Date.now().toString(36).slice(-4);
        const r = Math.random().toString(36).slice(2, 4);
        return t + r;
    }

    /**
     * Human-readable label for which cake a surcharge belongs to. Goes on the line as a VISIBLE
     * property, because the Shopify admin order screen is not something we can template — the
     * meaning has to live in the value itself.
     *
     * @param   {string} token the cake's id from newCakeToken()
     * @param   {string} date  the fulfilment date, already on the cake line
     * @returns {string}       e.g. "Bento Heart · 2026-08-30 · #k3f9a2"
     */
    cakeLabel(token, date) {
        const size = this.config.sizes?.find(s => s.handle === this.state.sizeHandle);
        const name = size ? (this.localeName(size) || size.name) : '';
        return [name, date, '#' + token].filter(Boolean).join(' · ');
    }

    /**
     * Rename the fulfilment properties so the ORDER reads correctly.
     *
     * The form fields are static (`properties[Delivery Method]`, `properties[Delivery Date]`),
     * so a pickup order used to show "Delivery Method: Pickup / Delivery Date: …" — nonsense
     * for someone collecting from the shop. The method is a HANDOVER method, and the date is a
     * pickup date or a delivery date depending on which was chosen.
     *
     * Applied to both add-to-cart paths (JSON and the multipart photo path), so they cannot drift.
     *
     * @param   {string} key    the property name as it came off the form
     * @param   {string} method the chosen handover method ('Pickup' | 'Delivery')
     * @returns {string}        the name to store on the line item
     */
    fulfilmentKey(key, method) {
        if (key === 'Delivery Method') return 'Handover Method';
        if (key === 'Delivery Date') return method === 'Pickup' ? 'Pickup Date' : 'Delivery Date';
        return key;
    }

    /**
     * Localise ONE cake-line property for the ORDER, not just for the cart.
     *
     * Shopify renders line-item properties raw at checkout, in the admin and in the
     * notification emails. `snippets/cart-line-property.liquid` translates them at DISPLAY
     * time, which could only ever fix the cart — so a Bulgarian customer paid a checkout
     * reading "Sponge: Cocoa / Color: lilac / Fillings: cherry, lemon-curd-blueberry".
     * Writing them localised at add-to-cart is the only thing every surface sees.
     *
     * The fee lines have done exactly this since day one (`Добавка`, `Пълнеж`, `Доставка`),
     * which is why they already read correctly everywhere. This is that pattern, applied to
     * the cake line.
     *
     * Labels are copied from cart-line-property.liquid — the words the cart has always shown —
     * so the two cannot drift into two different Bulgarian vocabularies. Values resolve through
     * `localeName` against the SAME config the UI rendered from, so the order says exactly what
     * the customer clicked.
     *
     * ⚠️ Runs at add-to-cart, i.e. AFTER variant selection. `sizeData.name` still drives variant
     * matching (see ~line 1544) and is untouched — only the value written onto the line changes.
     *
     * ⚠️ QuickOrder reads these keys off the order (`Size` is how it identifies a cake line at
     * all). Its `prop()` compatibility layer accepts both spellings and MUST be deployed first.
     *
     * Not applied on the English storefront: there the English keys already read correctly.
     *
     * @param   {string} key    the property name as it came off the form
     * @param   {*}      value  its value (a File for the reference photo)
     * @param   {string} method the chosen handover method ('Pickup' | 'Delivery')
     * @returns {[string, *]}   [name, value] to store on the line item
     */
    orderProperty(key, value, method) {
        const name = this.fulfilmentKey(key, method);
        const isBG = String(this.config.locale || 'bg').toLowerCase().startsWith('bg');
        if (!isBG) return [name, value];

        const LABELS = {
            'Size': 'Размер', 'Sponge': 'Блат', 'Fillings': 'Пълнеж', 'Color': 'Цвят',
            'Handover Method': 'Получаване', 'Delivery Zone': 'Квартал', 'Delivery Address': 'Адрес за доставка',
            'Pickup Date': 'Дата за вземане', 'Delivery Date': 'Дата за доставка',
            'Time Slot': 'Диапазон', 'Message': 'Надпис', 'Custom Text': 'Текст',
            'Notes': 'Инструкции', 'Reference Image': 'Снимка',
        };
        const label = LABELS[name] || name;
        // The reference photo is a File; rename the field but never touch the payload.
        if (typeof value !== 'string') return [label, value];

        // A handle with no match falls back to the handle rather than blanking — visibly
        // untranslated is a far better failure than an empty field on a cake someone bakes.
        const byHandle = (list, h) => this.localeName((list || []).find(x => x.handle === h)) || h;

        let out = value;
        switch (name) {
            case 'Sponge':
                out = value === 'Cocoa' ? 'Какао' : value === 'Vanilla' ? 'Ванилия' : value;
                break;
            case 'Handover Method':
                out = value === 'Pickup' ? 'Вземане от място' : value === 'Delivery' ? 'Доставка' : value;
                break;
            case 'Color':
                out = byHandle(this.config.colors, value);
                break;
            case 'Fillings':
                out = value.split(',').map(h => byHandle(this.config.fillings, h.trim())).join(', ');
                break;
            case 'Delivery Zone':
                out = byHandle(this.config.zones, value);
                break;
            case 'Size': {
                const size = (this.config.sizes || []).find(x => x.name === value);
                out = this.localeName(size) || value;
                break;
            }
            default:
                // Dates, time slot and every free-text field stay verbatim: that text is piped
                // onto the cake or read by a driver, so translating or recasing it would be wrong.
                break;
        }
        return [label, out];
    }

    /**
     * Add-to-cart for the case where the customer attached a reference photo.
     *
     * Shopify's JSON items API cannot carry a file, so the cake line has to go up as multipart
     * form-data — which in turn only accepts ONE item per request. Hence two calls: the cake
     * (with the photo, straight off the form) and then the fee lines as the normal JSON batch.
     *
     * Cake first is deliberate — if it fails we return early and never leave orphan fee lines
     * in the cart. The cost is cosmetic: Shopify prepends each add, so the fees end up listed
     * above the cake here, where the single-request path keeps the cake on top.
     *
     * @param   {Array}    validItems  [cake, ...fees] — already filtered and numeric-id'd
     * @returns {Response}             the response whose body the caller parses (fees if there
     *                                 were any, otherwise the cake's), so STEP 6/7 stay unchanged
     */
    async addCakeWithFile(validItems) {
        const root = window.Shopify.routes.root;
        const [cake, ...fees] = validItems;

        // Straight off the <form>, so the file input and every properties[…] field come along.
        const fd = new FormData(this.dom.form);
        fd.set('id', cake.id);
        fd.set('quantity', cake.quantity);

        // The JSON path drops blank properties; FormData does not, and empty ones would show up
        // on the order as noise ("Message: "). Strip them, but never touch the file field.
        // Also apply the same fulfilment renaming as the JSON path, so a pickup order does not
        // arrive saying "Delivery Date".
        for (const [key, value] of Array.from(fd.entries())) {
            if (!key.startsWith('properties[')) continue;
            if (typeof value === 'string' && value.trim() === '') {
                fd.delete(key);
                continue;
            }
            const bare = key.replace('properties[', '').replace(']', '');
            const [renamed, localised] = this.orderProperty(bare, value, this.state.deliveryMethod);
            if (renamed !== bare || localised !== value) {
                fd.delete(key);
                fd.append(`properties[${renamed}]`, localised);
            }
        }

        // The cake's token is NOT on the form — it is generated in addToCart and only exists on
        // the JSON payload. Without this the multipart (reference-photo) path would ship a cake
        // with no _Cake_ID while its add-ons carried one, so nothing could be joined back up:
        // exactly the bug this token exists to fix, surviving in the one path that skips it.
        //
        // EVERY hidden (`_`-prefixed) property is carried, not a named list: `_bh_state` (the
        // cart's Edit snapshot) was added later and silently fell through this exact gap, so
        // photo cakes had no Edit button. A named list would drop the next one the same way.
        Object.entries(cake.properties || {}).forEach(([k, v]) => {
            if (k.startsWith('_') && v != null && v !== '') fd.set(`properties[${k}]`, v);
        });

        const cakeResponse = await fetch(root + 'cart/add.js', { method: 'POST', body: fd });
        if (!cakeResponse.ok || !fees.length) return cakeResponse;

        const feeResponse = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: fees })
        });

        // ROLLBACK. This path is the only one that is not atomic: the JSON path sends the cake
        // and every surcharge in a SINGLE cart/add.js call, so it either all lands or none of it
        // does. Here the cake goes up as multipart (a file cannot ride the JSON items API) and
        // the fees follow separately, which opens a window where the cake is in the cart and its
        // surcharges are not — a customer checking out paying nothing for decorations the cake's
        // own properties still describe. That is exactly the desync the whole surcharge system
        // exists to prevent, so undo the cake rather than leave an undercharged cart behind.
        if (!feeResponse.ok) {
            try {
                // clone(): the caller still parses cakeResponse's body on some paths, and a body
                // can only be read once.
                const added = await cakeResponse.clone().json();
                const key = added && (added.key || (added.items && added.items[0] && added.items[0].key));
                if (key) {
                    await fetch(root + 'cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: key, quantity: 0 })
                    });
                    console.warn('Bakehunters: surcharges failed to add — rolled the cake back out of the cart');
                }
            } catch (err) {
                // Rolling back is best-effort. If it fails the customer still sees the error from
                // feeResponse and can retry; bh-cart-integrity will not help here because the
                // cake is the thing left over, not an orphaned fee.
                console.warn('Bakehunters: could not roll back the cake line', err);
            }
        }

        return feeResponse;
    }

    /**
     * Write a durable record of the surcharges this cake required into the CART ATTRIBUTES.
     *
     * Fee lines are removable in principle (and were removable in the UI until the
     * .cart-item--fee rules landed), but cart attributes survive to the order no matter what
     * happens to the lines. So the team can always compare "what was required" against "what
     * was actually charged" and phone the customer when they disagree — which is the whole
     * point for rush orders: a same-day cake that arrived without its €5 needs a human.
     *
     * Two attributes, deliberately:
     *   `Rush order`             — visible, localized, so it is unmissable on the order.
     *   `_bh_required_surcharges`— underscored + machine-readable, the full audit line.
     *                              Append-only, so several cakes in one cart each keep a row.
     *
     * @param {object}  cartData      the cart as just fetched (for its existing attributes)
     * @param {string}  date          the cake's delivery date, used as the row key
     * @param {boolean} needsRushFee  whether a rush fee was added for this cake
     */
    async recordRequiredSurcharges(cartData, date, needsRushFee) {
        try {
            const required = [];
            if (needsRushFee) required.push('rush:5');
            if (this.state.deliveryMethod === 'Delivery' && this.state.deliveryZone) {
                // `:free` keeps free-delivery orders countable, to see whether far zones abuse it.
                required.push(`zone:${this.state.deliveryZone}${this._freeDelivery() ? ':free' : ''}`);
            }
            const addonHandles = Object.keys(this.state.addons || {});
            if (addonHandles.length) required.push(`addons:${addonHandles.join('+')}`);
            const premium = (this.state.fillings || []).filter(h =>
                this.config.fillings?.some(f => f.handle === h && f.isPremium));
            if (premium.length) required.push(`premium:${premium.join('+')}`);

            const row = `${date} | ${required.join(' | ') || 'none'}`;
            const previous = cartData?.attributes?.['_bh_required_surcharges'];
            const attributes = { '_bh_required_surcharges': previous ? `${previous}\n${row}` : row };

            // Once set, the visible flag stays set — one rush cake makes the whole order a
            // rush order as far as the kitchen is concerned.
            if (needsRushFee || cartData?.attributes?.[this.t('rush_attr_label')]) {
                attributes[this.t('rush_attr_label')] = this.t('rush_attr_value');
            }

            // The attribute key is the translated label, so renaming "Бърза поръчка" to
            // "Експресна поръчка" orphaned the old key on carts that already had it: checkout
            // would list both. Setting an attribute to '' deletes it.
            // ponytail: delete this once no cart predates the rename, a few weeks at most.
            ['Бърза поръчка', 'Rush order'].forEach((legacyKey) => {
                if (cartData?.attributes?.[legacyKey] && !attributes[legacyKey]) {
                    attributes[legacyKey] = '';
                }
            });

            await fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attributes })
            });
            console.log('  - Recorded required surcharges:', row);
        } catch (err) {
            // Never block the add-to-cart on bookkeeping.
            console.warn('Could not record required surcharges on the cart', err);
        }
    }

    getProductHandle() {
        // Method 1: From URL path (e.g., /products/testova-torta)
        const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
        if (pathMatch) {
            return pathMatch[1];
        }

        // Method 2: From data attribute on container
        const container = document.getElementById('BakehuntersApp');
        if (container?.dataset.productHandle) {
            return container.dataset.productHandle;
        }

        // Method 3: From meta tag
        const metaHandle = document.querySelector('meta[property="og:url"]');
        if (metaHandle) {
            const urlMatch = metaHandle.content.match(/\/products\/([^/?#]+)/);
            if (urlMatch) return urlMatch[1];
        }

        return null;
    }

    refreshCartCount() {
        fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
                // Update all cart count elements (theme-specific selectors)
                document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-count]').forEach(el => {
                    el.textContent = cart.item_count;
                    if (cart.item_count > 0) el.style.display = 'block'; // Ensure visibility
                });
            })
            .catch(err => console.warn('Could not refresh cart count', err));
    }

    renderInitialState() {
        this.renderShapes();
        this.renderFillings();
        this.renderColors();
        this.renderAddons();
        this.renderZones();

        // 0. Reflect any pre-seeded add-ons (preset design or URL prefill) in the DOM:
        // check their boxes + reveal text inputs. State + price already account for them.
        if (this.state.addons && Object.keys(this.state.addons).length) {
            Object.keys(this.state.addons).forEach(handle => {
                const row = document.querySelector(`.bh-addon-row[data-handle="${handle}"]`);
                if (!row) return;
                const cb = row.querySelector('.bh-addon-checkbox');
                if (cb) cb.checked = true;
                const wrapper = row.querySelector('.bh-addon-input-wrapper');
                if (wrapper) wrapper.classList.remove('bh-is-hidden');
            });
        }

        // 1. Shape — seeded from the design (applyPresetSeed) or a URL param. If neither set
        // one (e.g. the custom-cake product), default to the first shape so the size list is
        // never gated behind an empty "choose a shape first" prompt (progressive disclosure
        // removed — everything is visible from the start).
        if (!this.state.shape) {
            this.state.shape = (this.config.sizes && this.config.sizes[0] && this.config.sizes[0].shape) || 'round';
        }
        this.selectShape(this.state.shape);

        // 2. Color Selection — respect an explicit URL param only; NO default (no
        // pre-loaded states — the customer must actively pick a colour).
        if (this.state.colorHandle) {
            this.selectColor(this.state.colorHandle);
        }

        // 3. Message (Respect URL param)
        if (this.state.message && this.dom.cakeMessage) {
            this.dom.cakeMessage.value = this.state.message;
        }

        // 4. Fillings (Respect URL param)
        if (this.state.fillings.length > 0) {
            this.updateFillingVisuals();
            this.updateFillingInput();
        }

        // Initialize delivery UI (hide zone dropdown for default "Pickup"). Also seeds the
        // time-slot <select> and the pickup-hours block.
        this.updateDeliveryUI();

        // Progressive gates: sponge (and fillings) stay gated behind a chosen size.
        this.setupSpongeListener();
        this.updateSpongeGate();

        // Clear the validation notice as soon as the customer interacts with any option.
        ['click', 'change'].forEach((evt) => this.dom.form.addEventListener(evt, (e) => {
            if (!e.target.closest('#AddToCartBtn')) this.clearValidationNotice();
        }));

        // Don't re-add an "ensure Time Slot has options" fallback here: empty IS the correct
        // state for Pickup, and seeding it on load is what put a delivery window on order
        // #1003. updateDeliveryUI() above is the only seeder.

        // Final Price validation
        this.calculateTotal();
    }

    // Progressive disclosure removed — sponge options are always visible (no "choose a size
    // first" gate). The whole cake stays configurable at a glance.
    updateSpongeGate() {
        const opts = document.getElementById('SpongeOptions');
        const gate = document.getElementById('SpongeGate');
        if (opts) opts.style.display = '';
        if (gate) gate.style.display = 'none';
    }

    setupSpongeListener() {
        if (this._spongeListenerAdded) return;
        document.querySelectorAll('input[name="properties[Sponge]"]').forEach((radio) => {
            radio.addEventListener('change', () => {
                if (radio.checked) this.state.sponge = radio.value;
            });
        });
        this._spongeListenerAdded = true;
    }

    // Returns the first missing required step { el, message } or null when complete.
    validateConfiguration() {
        const t = (k) => this.t(k);
        if (!this.state.shape) return { el: document.querySelector('.bh-shape-pill'), message: t('select_shape_first') };
        if (!this.state.sizeHandle) return { el: this.dom.sizePills, message: t('select_size_first') };
        if (!this.state.sponge) return { el: document.getElementById('SpongeOptions'), message: t('select_sponge') };
        if (!this.state.fillings || this.state.fillings.length < 1) return { el: this.dom.fillingOptions, message: t('select_filling') };
        if (!this.state.colorHandle) return { el: this.dom.colorSwatches || document.querySelector('.bh-swatch'), message: t('select_color') };
        // Add-ons sit between colour and delivery on the page (block order: … colour, addons,
        // …, delivery). Validation returns the FIRST problem and scrolls to it, so this has to
        // sit here — flagged after delivery it would scroll the customer backwards.
        const blankAddon = this.blankAddonText();
        if (blankAddon) return blankAddon;
        // Photo print ticked with no photo, or a photo with the "too detailed" question unanswered
        // (assets/bh-print-toggle.js). The photo block sits right after add-ons.
        const printGap = window.bhPrint ? window.bhPrint.missing() : null;
        if (printGap) return printGap;
        if (!this.state.deliveryMethod) return { el: document.getElementById('DeliveryMethodPills'), message: t('select_method') };
        if (this.state.deliveryMethod === 'Delivery' && !this.state.deliveryZone) {
            const wrap = document.getElementById('DeliveryZoneWrapper');
            // Flash only the dropdown field, not the whole delivery block.
            const field = wrap.querySelector('bh-select') || document.getElementById('DeliveryZoneSelect');
            return { el: wrap, flashEl: field, message: t('select_neighborhood') };
        }
        // Address after the neighbourhood, matching the page (assets/bh-address.js). It needs a
        // number (street or block); Google is not consulted, so an outage never blocks an order.
        const address = document.getElementById('DeliveryAddress');
        // BhZoneMatch is a script-level const (assets/bh-zone-match.js), not a window property.
        const hasNumber = typeof BhZoneMatch !== 'undefined' ? BhZoneMatch.hasNumber(address?.value, this.config.zones) : /\d/.test(address?.value || '');
        if (this.state.deliveryMethod === 'Delivery' && address && !hasNumber) {
            return { el: address.closest('bh-address') || address, flashEl: address, message: t('address_required') };
        }
        if (!this.state.date) return { el: document.querySelector('.bh-datetime'), message: t('select_date') };
        return null;
    }

    /**
     * A text add-on that is ticked but has nothing typed in it.
     *
     * For these, state.addons[handle] holds the STRING, not `true` — and ticking the box
     * writes '' before anyone types, so an empty value is precisely the failure state rather
     * than an impossible one. Unticked add-ons are deleted from state, so `undefined` means
     * "not selected" and must pass. Whitespace counts as empty: " " on the side of a cake is
     * nothing, and it would otherwise buy a €3 add-on that decorates air.
     *
     * Driven by the `requiresText` flag, not by the side-text handle, so a second text add-on
     * is covered the day it is added and needs no change here.
     */
    blankAddonText() {
        const needsText = (this.config.addons || []).filter((a) => a.requiresText);
        for (const addon of needsText) {
            const value = this.state.addons ? this.state.addons[addon.handle] : undefined;
            if (value === undefined) continue;
            if (String(value).trim()) continue;
            const row = document.querySelector(`.bh-addon-row[data-handle="${addon.handle}"]`);
            return {
                el: row || document.getElementById('AddonsContainer'),
                // Flash the input itself, the way the neighborhood dropdown does — the row is
                // mostly the label, and the empty field is the thing to go and fill in.
                flashEl: row ? row.querySelector('.bh-addon-text-input') : null,
                message: this.t('addon_text_required').replace('{addon}', this.localeName(addon)),
            };
        }
        return null;
    }

    clearValidationNotice() {
        const n = document.getElementById('ValidationNotice');
        if (n && !n.classList.contains('bh-is-hidden')) n.classList.add('bh-is-hidden');
    }

    flagMissing({ el, flashEl, message }) {
        // Toast, not an inline notice: submit auto-scrolls to the first missing field, so an
        // inline message pinned near the add-to-cart button scrolls off-screen and is never
        // seen. A fixed toast stays visible wherever the scroll lands.
        this.showToast(message);
        // Error haptic on phones (bh-haptic.js); silent unless a real tap caused this.
        if (window.bhHaptic) window.bhHaptic('error');
        if (el) {
            const step = el.closest('.bh-step, .bh-group, fieldset') || el;
            step.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Scroll centres the whole step, but the highlight can target a narrower element
            // (e.g. the neighborhood dropdown) via flashEl. Restart the pulse if re-flagged
            // (remove + reflow + add) and clear any pending removal so a stale timer can't cut
            // a fresh pulse short.
            const target = flashEl || step;
            target.classList.remove('bh-step--flash');
            void target.offsetWidth;
            target.classList.add('bh-step--flash');
            clearTimeout(target._flashTimer);
            target._flashTimer = setTimeout(() => target.classList.remove('bh-step--flash'), 1700);
        }
    }

    // Fixed, viewport-anchored error toast (auto-dismisses). Created once, reused.
    showToast(message) {
        let toast = document.getElementById('BhToast');
        if (!toast) {
            toast = document.createElement('div');
            toast.id = 'BhToast';
            toast.className = 'bh-toast';
            toast.setAttribute('role', 'alert');
            toast.setAttribute('aria-live', 'assertive');
            document.body.appendChild(toast);
        }
        toast.textContent = message;
        toast.classList.remove('bh-toast--show');
        void toast.offsetWidth; // restart the enter transition even on repeat clicks
        toast.classList.add('bh-toast--show');
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => toast.classList.remove('bh-toast--show'), 3600);
    }

    renderShapes() {
        if (!this.dom.shapePills) {
            console.error('Bakehunters: Shape pills container not found!');
            return;
        }

        console.log('Bakehunters: Rendering shape selection');

        // Render shape pills
        const iconBase = this.config.assetUrlPrefix || '';
        this.dom.shapePills.innerHTML = `
            <button type="button" class="bh-shape-pill" data-shape="round" role="radio" aria-checked="false">
                <img class="bh-card-icon" src="${iconBase}shape-round.png" alt="" aria-hidden="true" loading="lazy" width="24" height="24">
                <span class="bh-shape-name">${this.t('shape_round')}</span>
            </button>
            <button type="button" class="bh-shape-pill" data-shape="heart" role="radio" aria-checked="false">
                <img class="bh-card-icon" src="${iconBase}shape-heart.png" alt="" aria-hidden="true" loading="lazy" width="24" height="24">
                <span class="bh-shape-name">${this.t('shape_heart')}</span>
            </button>
        `;

        // Add event listener for shape selection
        if (!this._shapeListenerAdded) {
            this.dom.shapePills.addEventListener('click', (e) => {
                const pill = e.target.closest('.bh-shape-pill');
                if (pill && pill.dataset.shape) {
                    this.selectShape(pill.dataset.shape);
                }
            });
            this._shapeListenerAdded = true;
        }
    }

    selectShape(shape) {
        console.log('Bakehunters: Selecting shape:', shape);

        // Keep the chosen size when switching shape (round Bento → heart Bento, etc.).
        // Sizes share diameters across shapes (12/16/20/22/24), so remember the current
        // size's diameter and remap sizeHandle to the same-diameter size in the new shape.
        if (this.state.sizeHandle && this.state.shape !== shape) {
            const current = this.config.sizes.find(s => s.handle === this.state.sizeHandle);
            if (current) {
                const equivalent = this.config.sizes.find(s => s.shape === shape && s.diameter === current.diameter);
                if (equivalent) this.state.sizeHandle = equivalent.handle;
            }
        }

        this.state.shape = shape;

        // Visual Update - toggle active class + aria-checked for radio semantics
        document.querySelectorAll('.bh-shape-pill').forEach(el => {
            const isActive = el.dataset.shape === shape;
            el.classList.toggle('active', isActive);
            el.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });

        // Render sizes for selected shape
        this.renderSizes();
    }

    renderSizes() {
        if (!this.dom.sizePills) {
            console.error('Bakehunters: Size pills container not found!');
            return;
        }

        if (!this.state.shape) {
            console.log('Bakehunters: No shape selected yet');
            this.dom.sizePills.innerHTML = `<p class="bh-gate-hint">${this.t('select_shape_first')}</p>`;
            return;
        }

        // Filter sizes by selected shape, then order MOST-EXPENSIVE FIRST (reverse price
        // anchoring: the customer sees the top price first, so mid sizes feel reasonable).
        const filteredSizes = this.config.sizes
            .filter(size => size.shape === this.state.shape)
            .sort((a, b) =>
                parseFloat((b.basePrice && b.basePrice.amount) || b.basePrice || 0) -
                parseFloat((a.basePrice && a.basePrice.amount) || a.basePrice || 0)
            );

        console.log('Bakehunters: Rendering', filteredSizes.length, 'sizes for shape:', this.state.shape);

        // "от X €/парче" on every size except the cheapest (Bento): per portion it is the
        // priciest, so showing it there argues against the cake. Divides by the TOP of the
        // portion range. Nudges the upgrade at the moment sizes are compared.
        const cheapest = filteredSizes[filteredSizes.length - 1];
        const perPortion = size => {
            const hi = parseInt(String(size.portions).split('-').pop(), 10);
            if (size === cheapest || !hi) return '';
            const each = (parseFloat(size.basePrice.amount) / hi).toFixed(2);
            return `<span class="bh-pill-unit">${this.t('per_portion').replace('{price}', each)}</span>`;
        };

        // Render size pills
        this.dom.sizePills.innerHTML = filteredSizes.map(size => `
            <button type="button" class="bh-pill" data-handle="${size.handle}" role="radio" aria-checked="false">
                <span class="bh-pill-name">${this.sizeLabel(size)}</span>
                <span class="bh-pill-meta">${size.portions} ${this.t('portions')}</span>
                <span class="bh-pill-price">${parseFloat(size.basePrice.amount).toFixed(2)} €</span>
                ${perPortion(size)}
                ${BH_FREE_DELIVERY_SIZES.includes(size.handle.toLowerCase()) ? `<span class="bh-pill-perk"><span>${this.t('free_delivery')}</span></span>` : ''}
                ${BH_POPULAR_SIZES.includes(size.handle.toLowerCase()) ? `<span class="bh-pill-perk bh-pill-perk--popular"><span>${this.t('most_ordered')}</span></span>` : ''}
            </button>
        `).join('');
        // Pills re-rendered (e.g. shape switch) with nothing selected yet: clear the upsell.
        document.dispatchEvent(new CustomEvent('bh:size-change', { detail: { handle: null } }));

        // Add click listener (only once)
        if (!this._sizePillsListenerAdded) {
            this.dom.sizePills.addEventListener('click', (e) => {
                const pill = e.target.closest('.bh-pill');
                if (pill && pill.dataset.handle) {
                    this.selectSize(pill.dataset.handle);
                }
            });
            this._sizePillsListenerAdded = true;
        }

        // No size is pre-selected — the customer must actively pick one (no pre-loaded
        // states; the price stays "От {min}" until then). Only honor an explicit URL
        // param (personalize-from-preset transfer).
        if (this.state.sizeHandle && filteredSizes.some(s => s.handle === this.state.sizeHandle)) {
            this.selectSize(this.state.sizeHandle);
        }
    }

    selectSize(handle) {
        console.log('Bakehunters: Selecting size:', handle);

        this.state.sizeHandle = handle;
        // Unlock the size-gated steps (sponge + fillings) now that a size exists.
        this.updateSpongeGate();
        this.renderFillings();
        this.updateFillingVisuals();
        const sizeData = this.config.sizes.find(s => s.handle === handle);

        if (!sizeData) {
            console.error('Bakehunters: Size data not found for handle:', handle);
            return;
        }

        this._relabelZones();

        // Visual Update - toggle active class + aria-checked
        document.querySelectorAll('.bh-pill').forEach(el => {
            const isActive = el.dataset.handle === handle;
            el.classList.toggle('active', isActive);
            el.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });

        // Message Limit Update
        if (sizeData) {
            if (this.dom.maxChar) this.dom.maxChar.textContent = sizeData.messageLimit;
            if (this.dom.cakeMessage) this.dom.cakeMessage.setAttribute('maxlength', sizeData.messageLimit);

            // Update the delivery calendar's lead time so it re-disables too-soon dates for
            // this size (the <bh-date-picker> re-renders and deselects an invalid date).
            const minLead = parseInt(sizeData.minLeadDays) || 0;
            if (this.dom.datePicker) {
                this.dom.datePicker.setAttribute('min-lead-days', String(minLead));
            }
            // If a previously chosen date is now too soon for this size, clear it silently
            // and refresh the time slots (the calendar has already deselected it).
            if (this.state.date) {
                const minDate = new Date();
                minDate.setHours(0, 0, 0, 0);
                minDate.setDate(minDate.getDate() + minLead);
                if (new Date(this.state.date + 'T00:00:00') < minDate) {
                    this.state.date = null;
                    if (this.dom.deliveryDate) this.dom.deliveryDate.value = '';
                    this.updateTimeSlots();
                    this.updatePickupHours();
                }
            }
        }
        // bh-size-upsell.js listens: offers the next size up for some sizes. After the lead-time
        // check above, so it sees the date as it stands for the new size.
        document.dispatchEvent(new CustomEvent('bh:size-change', { detail: { handle } }));

        // VARIANT SELECTION - Use product's variants, fallback to original
        // Since this is a configurator with customization, size selection affects LINE ITEM PROPERTIES
        // not necessarily the variant (unless we have size-specific variants)

        // Helper: Normalize handle by removing hyphens and spaces
        const normalizeHandle = (str) => {
            return str.toLowerCase().replace(/[-\s]/g, '');
        };

        // VARIANT SELECTION logic
        // If we have specific variants for sizes, try to find them.
        // If not (e.g. single "Default Title" variant), keep the original ID.

        let variantIdToUse = this.originalVariantId;
        let matchedVariantPrice = null;

        if (this.productVariants && this.productVariants.length > 0) {
            const normalizedSizeHandle = normalizeHandle(handle);

            // Method 1: Exact match on sizeHandle (if metafield is set)
            let matchingVariant = this.productVariants.find(v => v.sizeHandle === handle);

            // Method 2: Normalized handle match (removes hyphens/spaces)
            if (!matchingVariant) {
                matchingVariant = this.productVariants.find(v =>
                    normalizeHandle(v.sizeHandle) === normalizedSizeHandle
                );
                if (matchingVariant) {
                    console.log('Bakehunters: Matched via normalized handle:', normalizedSizeHandle);
                }
            }

            // Method 3: Fuzzy match by size name
            if (!matchingVariant && sizeData) {
                const normalizedSizeName = normalizeHandle(sizeData.name);
                matchingVariant = this.productVariants.find(v =>
                    normalizeHandle(v.title) === normalizedSizeName
                );
                if (matchingVariant) {
                    console.log('Bakehunters: Matched via normalized name:', sizeData.name);
                }
            }

            // Method 4: Partial name match (fallback)
            if (!matchingVariant && sizeData) {
                matchingVariant = this.productVariants.find(v =>
                    v.title.toLowerCase().includes(sizeData.name.toLowerCase()) ||
                    sizeData.name.toLowerCase().includes(v.title.toLowerCase())
                );
                if (matchingVariant) {
                    console.log('Bakehunters: Matched via partial name match');
                }
            }

            if (matchingVariant && matchingVariant.id) {
                variantIdToUse = matchingVariant.id;
                matchedVariantPrice = matchingVariant.price / 100; // Convert from cents to EUR
                console.log('Bakehunters: Found matching variant:', matchingVariant.title, 'ID:', variantIdToUse, 'Price:', matchedVariantPrice);
            } else {
                console.warn('Bakehunters: No matching variant found for size:', handle);
                // DANGEROUS FALLBACK REMOVED: Do not default to the first variant as it might be an expensive "Party Size" variant.
                // We should rely on the metaobject price or the original variant.
                // if (this.productVariants.length === 1) { ... }
            }
        } else {
            console.log('Bakehunters: No variant data available. Using original ID:', variantIdToUse);
        }

        // Update state with selected variant ID
        this.state.selectedVariantId = variantIdToUse;

        // Update Hidden Input
        if (variantIdToUse && this.dom.variantInput) {
            this.dom.variantInput.value = variantIdToUse;
        }

        // PRICE PRIORITY: Use metaobject price FIRST (these are the updated EUR prices)
        // Only fall back to variant price if metaobject price is not available
        if (sizeData && sizeData.basePrice) {
            // Use metaobject price - these are the correct EUR prices from API
            this.state.basePrice = parseFloat(sizeData.basePrice.amount || sizeData.basePrice || 0);
            console.log('Bakehunters: Using metaobject base price:', this.state.basePrice);
        } else if (matchedVariantPrice !== null && matchedVariantPrice > 0) {
            // Fallback to variant price if metaobject is not available
            this.state.basePrice = matchedVariantPrice;
            console.log('Bakehunters: Using variant base price (fallback):', matchedVariantPrice);
        }

        // Always update Size as a property (crucial for single-variant products)
        this.updateSizeProperty(sizeData);

        // Re-check rush availability (fee might change based on size)
        this.checkRushAndAvailability();

        // Disable any addons that don't fit this size (none currently).
        this.updateAddonAvailability(sizeData);

        this.calculateTotal();
    }

    // Which addons cannot be applied to which size handles. Empty now (fondant-text add-on
    // removed); the mechanism stays for any future size-restricted add-on.
    get sizeRestrictedAddons() {
        return {};
    }

    updateAddonAvailability(sizeData) {
        if (!sizeData) return;
        const restrictions = this.sizeRestrictedAddons;
        Object.entries(restrictions).forEach(([addonHandle, blockedSizes]) => {
            const row = document.querySelector(`.bh-addon-row[data-handle="${addonHandle}"]`);
            if (!row) return;
            const isBlocked = blockedSizes.includes(sizeData.handle);
            row.classList.toggle('bh-addon-row--disabled', isBlocked);
            const checkbox = row.querySelector('.bh-addon-checkbox');
            // If this addon was selected and is now blocked, auto-deselect
            if (isBlocked && checkbox && checkbox.checked) {
                checkbox.checked = false;
                delete this.state.addons[addonHandle];
                const wrapper = row.querySelector('.bh-addon-input-wrapper');
                const textInput = row.querySelector('.bh-addon-text-input');
                if (textInput) textInput.value = '';
                if (wrapper) wrapper.classList.add('bh-is-hidden');
            }
        });
    }

    updateSizeProperty(sizeData) {
        let input = document.querySelector('input[name="properties[Size]"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[Size]';
            this.dom.form.appendChild(input);
        }
        input.value = sizeData ? sizeData.name : '';
    }

    // "Large" tier = ONLY L / XL / XXL, never Bento (Bento Classic and Bento Maxi price as
    // Standard). Deliberately has no portions-based fallback — that used to misclassify Bento
    // as Large. Shared by the price badge and calculateTotal so the displayed fee and the
    // charged fee cannot drift apart.
    // NOTE: addToCart still inlines its own copy of this test (with the old portions clause).
    // Harmless for today's sizes, but it should be folded into this helper.
    isLargeTier() {
        const sizeData = this.config.sizes.find(s => s.handle === this.state.sizeHandle);
        return !!(sizeData && (
            sizeData.tier === 'large' ||
            ['l', 'xl', 'xxl', 'heart-l', 'heart-xl', 'heart-xxl', 'circlel', 'circlexl', 'circlexxl', 'heartl', 'heartxl', 'heartxxl'].includes(sizeData.code?.toLowerCase())
        ));
    }

    renderFillings() {
        if (!this.dom.fillingOptions) return;

        // Progressive disclosure removed — fillings always render. Their premium fee is
        // applied by the size tier in calculateTotal once a size is set (always is, since the
        // preset seeds one), so nothing depends on gating the list.
        console.log('Bakehunters: Rendering fillings:', this.config.fillings);

        // Render without hidden checkboxes - state is managed purely in JS
        const iconBase = this.config.assetUrlPrefix || '';
        const FILLING_ICONS = BH_FILLING_ICONS;
        const isLargeTier = this.isLargeTier();

        // Order comes from the metaobject's sort_order, not from whatever order the API
        // returned. Ties keep their existing relative order (sort is stable), so an unset
        // sort_order does not shuffle the list around.
        const ordered = [...this.config.fillings].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));

        this.dom.fillingOptions.innerHTML = ordered.map(f => {
            // Show the fee for the SELECTED size tier. It used to always print premiumFee, so a
            // Large cake advertised the Standard price and then charged the Large one at checkout.
            const feeSource = isLargeTier && f.premiumFeeLarge ? f.premiumFeeLarge : f.premiumFee;
            const fee = f.isPremium ? bhMoney(feeSource) : 0;
            const priceBadge = f.isPremium ? `<span class="bh-checkbox-badge">+${fee} €</span>` : '';
            const newBadge = f.isNew ? `<span class="bh-checkbox-badge bh-checkbox-badge--new">${this.t('filling_new')}</span>` : '';
            const iconFile = FILLING_ICONS[f.handle];
            const icon = iconFile ? `<img class="bh-card-icon" src="${iconBase}${iconFile}" alt="" aria-hidden="true" loading="lazy" width="28" height="28">` : '';
            return `
        <div class="bh-checkbox-card${f.isPremium ? ' bh-checkbox-card--premium' : ''}" data-handle="${f.handle}">
           ${icon}
           <span class="bh-checkbox-name">${this.localeName(f)}</span>
           ${newBadge}
           ${priceBadge}
        </div>
      `;
        }).join('');

        // Use event delegation - single listener on container (no need to clone)
        // Remove any existing listener first
        if (this._fillingClickHandler) {
            this.dom.fillingOptions.removeEventListener('click', this._fillingClickHandler);
        }

        this._fillingClickHandler = (e) => {
            const card = e.target.closest('.bh-checkbox-card');
            if (!card) return;

            e.preventDefault();
            e.stopPropagation();

            const handle = card.dataset.handle;
            if (handle) {
                this.handleFillingClick(handle);
            }
        };

        this.dom.fillingOptions.addEventListener('click', this._fillingClickHandler);
    }

    handleFillingClick(handle) {
        // 1. Update State
        const index = this.state.fillings.indexOf(handle);
        const isCurrentlySelected = index > -1;

        if (isCurrentlySelected) {
            this.state.fillings.splice(index, 1);
            console.log('Bakehunters: Removed filling:', handle);
        } else {
            // Combinations we do not sell (assets/bh-filling-rules.js): the card is greyed out,
            // and tapping it still asks whether to swap it in for what it conflicts with.
            if (window.BhFillingRules?.blocker(this.state.fillings, handle)) {
                this.askFillingSwap(handle);
                return;
            }
            if (this.state.fillings.length >= 2) {
                alert(this.t('max_fillings'));
                return; // State does not change
            }
            this.state.fillings.push(handle);
            console.log('Bakehunters: Added filling:', handle);
        }

        // 2. Sync UI to State (Single Source of Truth)
        this.updateFillingVisuals();

        // 3. Update Form Data & Price
        this.updateFillingInput();
        this.calculateTotal();
    }

    updateFillingVisuals() {
        if (!this.dom.fillingOptions) return;
        const cards = this.dom.fillingOptions.querySelectorAll('.bh-checkbox-card');

        cards.forEach(card => {
            const handle = card.dataset.handle;
            const isSelected = this.state.fillings.includes(handle);

            // Simple class-based state management
            card.classList.toggle('selected', isSelected);
            const blocked = !isSelected && !!window.BhFillingRules?.blocker(this.state.fillings, handle);
            card.classList.toggle('bh-checkbox-card--blocked', blocked);
        });
        this.updateFillingNote();
        console.log('Bakehunters: Visuals synced. Active:', this.state.fillings);
    }

    /** "Not a good mix: replace X with Y?" Yes = Y replaces whatever it conflicts with. */
    askFillingSwap(handle) {
        const R = window.BhFillingRules;
        const block = R.blocker(this.state.fillings, handle);
        const next = R.replace(this.state.fillings, handle);
        const removed = this.state.fillings.filter((h) => !next.includes(h));
        const name = (h) => this.localeName(this.config.fillings.find((f) => f.handle === h) || { nameBG: h, nameEN: h });
        const reason = block.reason === 'solo'
            ? this.t('filling_solo').replace('{name}', name(block.with))
            : this.t('filling_pair').replace('{a}', name(block.with)).replace('{b}', name(handle).toLowerCase());
        const question = this.t('filling_swap_q')
            .replace('{old}', removed.map((h) => name(h).toLowerCase()).join(this.t('filling_and')))
            .replace('{new}', name(handle).toLowerCase());
        R.ask({
            body: `${reason} ${question}`,
            opener: this.dom.fillingOptions.querySelector(`[data-handle="${handle}"]`),
            onYes: () => {
                this.state.fillings = next;
                this.updateFillingVisuals();
                this.updateFillingInput();
                this.calculateTotal();
            },
        });
    }

    /** One line under the fillings saying why a card is greyed out. Empty when nothing is. */
    updateFillingNote() {
        const R = window.BhFillingRules;
        if (!R || !this.dom.fillingOptions) return;
        let note = document.getElementById('BhFillingNote');
        if (!note) {
            note = document.createElement('p');
            note.id = 'BhFillingNote';
            note.className = 'bh-filling-note';
            note.setAttribute('aria-live', 'polite');
            this.dom.fillingOptions.after(note);
        }
        const name = (h) => this.localeName(this.config.fillings.find((f) => f.handle === h) || { nameBG: h, nameEN: h });
        // Only about what they HAVE chosen; a greyed-out card explains itself when tapped.
        const sel = this.state.fillings;
        const lines = [];
        R.SOLO.filter((h) => sel.includes(h)).forEach((h) => lines.push(this.t('filling_solo').replace('{name}', name(h))));
        R.NOT_TOGETHER.forEach((group) => {
            const picked = group.find((h) => sel.includes(h));
            if (!picked) return;
            const others = group.filter((h) => h !== picked && this.config.fillings.some((f) => f.handle === h));
            lines.push(this.t('filling_group').replace('{a}', name(picked))
                .replace('{others}', others.map((h) => name(h).toLowerCase()).join(this.t('filling_and'))));
        });
        note.textContent = lines.join(' ');
        note.hidden = !lines.length;
    }

    toggleFilling(handle) {
        // Legacy method support
        const card = document.querySelector(`.bh-checkbox-card[data-handle="${handle}"]`);
        if (card) {
            this.handleFillingClick(handle);
        } else {
            console.warn('Bakehunters: toggleFilling called but card not found:', handle);
        }
    }

    updateFillingInput() {
        // Create or update hidden input for fillings
        let input = document.querySelector('input[name="properties[Fillings]"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[Fillings]';
            this.dom.form.appendChild(input);
        }
        input.value = this.state.fillings.join(', ');
    }

    renderColors() {
        if (!this.dom.colorSwatches) return;
        // All swatches always visible. Dark colors auto-apply the surcharge.
        this.dom.colorSwatches.innerHTML = this.config.colors.map(c => `
            <button type="button"
                 class="bh-swatch"
                 style="background-color: ${c.hex};"
                 data-handle="${c.handle}"
                 data-dark="${c.isDark}"
                 role="radio"
                 aria-checked="false"
                 aria-label="${this.localeName(c)}"
                 title="${this.localeName(c)}">
            </button>
        `).join('');
        // Attach click handler (once)
        if (!this._colorListenerAdded) {
            this.dom.colorSwatches.addEventListener('click', (e) => {
                const swatch = e.target.closest('.bh-swatch');
                if (swatch && swatch.dataset.handle) {
                    this.selectColor(swatch.dataset.handle);
                }
            });
            this._colorListenerAdded = true;
        }
    }

    selectColor(handle) {
        const color = this.config.colors.find(c => c.handle === handle);

        this.state.colorHandle = handle;
        const colorInput = document.getElementById('ColorInput');
        if (colorInput) colorInput.value = handle;

        // Auto-apply dark-color surcharge — same line-item addon as before, but
        // driven by color choice instead of a user-checked addon row.
        this.state.addons = this.state.addons || {};
        if (color && color.isDark) {
            this.state.addons['dark-colors'] = true;
        } else {
            delete this.state.addons['dark-colors'];
        }

        document.querySelectorAll('.bh-swatch').forEach(el => {
            const isActive = el.dataset.handle === handle;
            el.classList.toggle('active', isActive);
            el.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });

        // Reflect the chosen colour's name next to the "Colours" label.
        const nameEl = document.querySelector('[data-color-name]');
        if (nameEl) nameEl.textContent = color ? this.localeName(color) : '';

        // Show the staining warning badge only for deep-pigment colours (black, navy, red…).
        const warnBadge = document.querySelector('.bh-color-warn');
        if (warnBadge) warnBadge.classList.toggle('bh-color-warn--visible', this.colorStainsHeavily(color));

        this.calculateTotal();
    }

    toggleDarkSwatches(unlocked) {
        console.log('Bakehunters: Dark swatches', unlocked ? 'SHOWN' : 'HIDDEN');

        // Show/hide dark swatches
        document.querySelectorAll('.bh-swatch').forEach(el => {
            const isDark = el.dataset.dark === 'true';
            if (isDark) {
                el.style.display = unlocked ? '' : 'none';
                if (!unlocked) el.classList.remove('active');
            }
        });

        // If hiding and a dark color is selected, reset to first light color
        if (!unlocked && this.state.colorHandle) {
            const currentColor = this.config.colors.find(c => c.handle === this.state.colorHandle);
            if (currentColor && currentColor.isDark) {
                const firstLight = this.config.colors.find(c => !c.isDark);
                if (firstLight) {
                    this.selectColor(firstLight.handle);
                } else {
                    this.state.colorHandle = null;
                }
            }
        }
    }

    renderZones() {
        if (!this.dom.deliveryZoneSelect) {
            console.error('Bakehunters: Delivery zone select element not found!');
            return;
        }

        console.log('Bakehunters: Rendering delivery zones:', this.config.zones?.length || 0);

        if (!this.config.zones || this.config.zones.length === 0) {
            console.warn('Bakehunters: No zones available in config!');
            return;
        }

        // Clear existing options (keep default/placeholder if any, or clear all)
        this.dom.deliveryZoneSelect.innerHTML = `<option value="" disabled selected>${this.t('select_neighborhood')}</option>`;

        // Group Zones
        const earlyZones = this.config.zones.filter(z => z.type === 'early').sort((a, b) => a.nameBG.localeCompare(b.nameBG));
        const lateZones = this.config.zones.filter(z => z.type === 'late').sort((a, b) => a.nameBG.localeCompare(b.nameBG));

        // Only one name is visible at a time, but people type the other one — a Bulgarian
        // customer on /en searches "Лозенец", an expat on the BG store types "Lozenets".
        // bh-select matches data-search, so put both names (+ the handle) there.
        const zoneHaystack = z => `${z.nameBG} ${z.nameEN} ${z.handle.replace(/-/g, ' ')}`;

        // Render Early Group
        if (earlyZones.length > 0) {
            const group = document.createElement('optgroup');
            group.label = this.t('early_delivery'); // Or 21:00 based on previous logic, label as requested
            earlyZones.forEach(z => {
                const opt = document.createElement('option');
                opt.value = z.handle;
                opt.textContent = this._zoneLabel(z);
                opt.dataset.search = zoneHaystack(z);
                group.appendChild(opt);
            });
            this.dom.deliveryZoneSelect.appendChild(group);
        }

        // Render Late Group
        if (lateZones.length > 0) {
            const group = document.createElement('optgroup');
            group.label = this.t('late_delivery');
            lateZones.forEach(z => {
                const opt = document.createElement('option');
                opt.value = z.handle;
                opt.textContent = this._zoneLabel(z);
                opt.dataset.search = zoneHaystack(z);
                group.appendChild(opt);
            });
            this.dom.deliveryZoneSelect.appendChild(group);
        }

        console.log('Bakehunters: Zone dropdown populated with grouped options');
    }

    // Pickup: no time slot is recorded, so show the shop's opening hours for the chosen day
    // instead ("Неделя, 23 август · Отворено 17:00 – 19:00") — otherwise nothing on the page
    // says Sunday closes at 19:00. Hidden on Delivery and until a date is picked. Mon/Tue are
    // unselectable in bh-date-picker (_blockReason → 'closed'), so a closed day cannot reach
    // here; the date guard below covers it defensively rather than rendering a closed state.
    updatePickupHours() {
        const el = document.getElementById('PickupHours');
        if (!el) return;

        const key = this.state.date;
        if (this.state.deliveryMethod !== 'Pickup' || !key) {
            el.hidden = true;
            return;
        }

        const [y, m, d] = String(key).split('-').map(Number);
        const day = new Date(y, m - 1, d);
        if (isNaN(day)) { el.hidden = true; return; }

        const locale = this.config.locale === 'en' ? 'en' : 'bg';
        const date = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(day);
        const when = date.charAt(0).toLocaleUpperCase(locale) + date.slice(1); // Bulgarian weekdays come lowercase
        const hours = this.t(day.getDay() === 0 ? 'hours_val_sun' : 'hours_val_open');
        const line = el.querySelector('[data-bh-hours-line]');
        if (line) line.textContent = `${when} · ${this.t('pickup_hours_open').replace('{hours}', hours)}`;
        el.hidden = false;
    }

    updateDeliveryUI() {
        console.log('Bakehunters: Updating delivery UI. Method:', this.state.deliveryMethod);

        // Single reaction point for a method change (the pill handler and init both land
        // here), so switching Delivery → Pickup actually clears the time-slot <select>.
        this.updateTimeSlots();
        this.updatePickupHours();

        if (!this.dom.deliveryZoneWrapper) {
            console.error('Bakehunters: Delivery zone wrapper element not found!');
            return;
        }

        const timeSlotWrapper = document.getElementById('TimeSlotWrapper');
        const dateLabel = document.getElementById('DeliveryDateLabel');
        const dateTimeGrid = document.querySelector('.bh-datetime');

        // No method chosen yet — hide the zone + date/time area entirely (progressive:
        // the customer picks pickup or delivery first; nothing about the date is shown
        // or pre-selected until then).
        if (!this.state.deliveryMethod) {
            this.dom.deliveryZoneWrapper.style.display = 'none';
            if (timeSlotWrapper) timeSlotWrapper.style.display = 'none';
            if (dateTimeGrid) dateTimeGrid.style.display = 'none';
            return;
        }
        if (dateTimeGrid) dateTimeGrid.style.display = '';

        if (this.state.deliveryMethod === 'Delivery') {
            this.dom.deliveryZoneWrapper.style.display = 'block';
            if (timeSlotWrapper) timeSlotWrapper.style.display = 'block';
            // Delivery: show the time-slot column beside the calendar (2-up layout).
            if (dateTimeGrid) dateTimeGrid.classList.remove('bh-datetime--pickup');
            if (this.dom.timeSlotSelect) this.dom.timeSlotSelect.required = true;
            if (dateLabel) dateLabel.textContent = this.t('delivery_date');

            console.log('Bakehunters: Zone dropdown shown');
        } else {
            this.dom.deliveryZoneWrapper.style.display = 'none';
            // Hide Time Slot for Pickup (using wrapper, not parent)
            if (timeSlotWrapper) timeSlotWrapper.style.display = 'none';
            // Pickup: no time slots (store hours) — collapse to a single compact column.
            if (dateTimeGrid) dateTimeGrid.classList.add('bh-datetime--pickup');
            if (this.dom.timeSlotSelect) this.dom.timeSlotSelect.required = false;
            if (dateLabel) dateLabel.textContent = this.t('pickup_date');

            this.state.deliveryZone = null;
            if (this.dom.deliveryZoneSelect) {
                this.dom.deliveryZoneSelect.value = "";
            }
            console.log('Bakehunters: Zone dropdown hidden');
        }
    }

    checkRushAndAvailability() {
        // Hidden first so every early return below leaves it off; shown at the end only once
        // the date is known to be fee-free.
        this.dom.noRushNote?.classList.add('bh-is-hidden');
        if (!this.state.date || !this.state.sizeHandle) return;

        const selectedDate = new Date(this.state.date + 'T00:00:00'); // Force local timezone
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const sizeData = this.config.sizes.find(s => s.handle === this.state.sizeHandle);
        if (!sizeData) return;

        // Calculate days difference
        const diffTime = selectedDate.getTime() - today.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        // Check minimum lead days
        const minLead = parseInt(sizeData.minLeadDays) || 0;

        // FIX: Allow rush orders if size allows it, even if under minLead days (implies rush fee)
        // Only block if under minLead AND rush is NOT allowed
        if (diffDays < minLead && !sizeData.rushAllowed) {
            alert(this.t('min_lead').replace('{days}', minLead));
            this.dom.deliveryDate.value = '';
            this.state.date = null;
            this.state.rushFee = 0;
            this.dom.rushFeeNotice.style.display = 'none';
            this._setRushExplainer(false);
            return;
        }

        // Check rush eligibility (Same Day OR Next Day logic)
        // Scope says: 
        // Small (Bento): Same Day (order by 12) = +15lv. 1 Day Before = No fee? Check scope.
        // Scope 8.2: 
        // Small: 3+ days (No), 2 days (No), 1 day (No), Same Day (+15lv/Free)
        // Big (Maxi+): 3+ days (No), 2 days (No), 1 day (+15lv/Free), Same Day (Not available)

        this.state.rushFee = 0;
        let requiresRushFee = false;
        let isSameDay = (diffDays === 0);
        let isNextDay = (diffDays === 1);

        const currentHour = new Date().getHours();
        const rushCutoff = this.config.shop?.rushCutoffHour || 12;

        const isSmall = !['large'].includes(sizeData.tier); // Bento Classic, Bento Heart, etc.
        // Note: Check Config 'tier' property logic properly. 
        // Scope: "Bigger" = Maxi and above. "Small" = Bento Classic, Bento Heart. 
        // My config marks Maxi as "standard" tier for pricing, but Scope 8.2 says "Bigger (Maxi and above)" has different rush rules.
        // I need to check SIZE CODE specifically.
        const isBentoSize = ['bento', 'bento-heart', 'bentoclassic', 'bentoheart'].includes(sizeData.code);

        if (isSameDay) {
            if (isBentoSize && currentHour < rushCutoff) {
                requiresRushFee = true;
            } else {
                alert(this.t('same_day_unavailable'));
                this.dom.deliveryDate.value = '';
                this.state.date = null;
                // This path bailed without clearing either notice, so a rush warning from the
                // previous pick stayed on screen against a date that had just been rejected.
                this.dom.rushFeeNotice.style.display = 'none';
                this._setRushExplainer(false);
                return;
            }
        } else if (isNextDay) {
            // ALL sizes have rush fee when ordering for next day
            requiresRushFee = true;
        }

        if (requiresRushFee) {
            const baseRushFee = parseFloat(sizeData.rushFee) || 5;
            const loyaltyThreshold = this.config.shop?.loyaltyThreshold || 1;
            const customer = this.config.customer || {};

            if (customer.isLoggedIn && customer.ordersCount >= loyaltyThreshold) {
                // Loyalty waiver: the fee is 0, so there is no fee line to show. Hiding beats
                // a green "waived!" banner that used to overwrite the notice's markup and
                // destroy the #RushFeeAmount span for the rest of the session.
                // ponytail: no waiver message, because the store has no accounts yet. Add one
                // here if customer accounts ever ship.
                this.state.rushFee = 0;
                this._setRushExplainer(false);
                this.dom.rushFeeNotice.style.display = 'none';
            } else {
                this.state.rushFee = baseRushFee;
                this._setRushExplainer(true);
                // Only the number is dynamic. The notice's own markup already says "Включва
                // <amount> такса за бърза поръчка", which is a receipt line next to the price;
                // the rule and the reason live in the callout by the date picker. This used to
                // overwrite the whole node with a second copy of that warning plus a login CTA
                // for accounts the store does not have.
                if (this.dom.rushFeeAmount) {
                    this.dom.rushFeeAmount.textContent = `${this.state.rushFee.toFixed(2)} €`;
                }
                this.dom.rushFeeNotice.style.display = 'block';
            }
        } else {
            this.dom.rushFeeNotice.style.display = 'none';
            this._setRushExplainer(false);
        }

        // Zero price bias: name the fee they avoided. Only for sizes that CAN carry a rush fee,
        // so an L ordered a week out is not told it escaped a charge it could never get.
        if (sizeData.rushAllowed && this.state.rushFee === 0) {
            this.dom.noRushNote?.classList.remove('bh-is-hidden');
        }

        this.calculateTotal();
    }

    /**
     * The rule callout under the date picker. Shown only while the chosen date is actually
     * being charged, so it never explains a fee the customer is not paying. The loyalty
     * waiver hides it for the same reason: the fee is waived, so there is nothing to explain.
     */
    /** True when the chosen size ships free to every zone (XL / XXL). */
    _freeDelivery() {
        return BH_FREE_DELIVERY_SIZES.includes(String(this.state.sizeHandle || '').toLowerCase());
    }

    /** Neighbourhood option text: the zone fee, or "Безплатно" on a free-delivery size. */
    _zoneLabel(z) {
        return `${this.localeName(z)} (${this._freeDelivery() ? this.t('free') : `+${bhMoney(z.fee)} €`})`;
    }

    /** Re-label the neighbourhood options after a size change (bh-select re-syncs on its own). */
    _relabelZones() {
        const select = this.dom.deliveryZoneSelect;
        if (!select || !this.config.zones) return;
        select.querySelectorAll('option[value]:not([value=""])').forEach(opt => {
            const z = this.config.zones.find(zn => zn.handle === opt.value);
            if (z) opt.textContent = this._zoneLabel(z);
        });
    }

    _setRushExplainer(visible) {
        if (this.dom.rushExplainer) {
            this.dom.rushExplainer.classList.toggle('bh-is-hidden', !visible);
        }
    }

    renderAddons() {
        const container = document.getElementById('AddonsContainer');
        if (!container || !this.config.addons) {
            console.log('Bakehunters: AddonsContainer not found or no addons config');
            return;
        }

        this.state.addons = this.state.addons || {}; // { handle: textValue_or_true }

        // Hide "dark-colors" — it's now an automatic surcharge when a dark color
        // is picked, not a user-facing addon. Variant + price logic still applies it.
        // Photo print needs the upload field, which only the custom-cake template has.
        const hasUpload = !!document.getElementById('CakeReferenceImage');
        const visibleAddons = this.config.addons.filter(a => a.handle !== 'dark-colors' && (!a.needsPhoto || hasUpload));

        const iconBase = this.config.assetUrlPrefix || '';
        const ADDON_ICONS = BH_ADDON_ICONS;

        container.innerHTML = visibleAddons.sort((a, b) => a.order - b.order).map(addon => {
            const iconFile = ADDON_ICONS[addon.handle];
            const icon = iconFile ? `<img class="bh-addon-icon" src="${iconBase}${iconFile}" alt="" aria-hidden="true" loading="lazy" width="28" height="28">` : '';
            return `
            <div class="bh-addon-row" data-handle="${addon.handle}">
                <label class="bh-addon-label">
                    <input type="checkbox" class="bh-addon-checkbox" value="${addon.handle}">
                    ${icon}
                    <span class="bh-addon-name">${this.localeName(addon)}</span>
                    <span class="bh-addon-price">+<span class="addon-price-dynamic">${addon.priceStandard.toFixed(2)}</span> €</span>
                </label>
                ${addon.requiresText ? `
                <div class="bh-addon-input-wrapper bh-is-hidden" data-bh-collapse>
                    <input type="text" class="bh-addon-text-input" placeholder="${this.t('enter_text')}" maxlength="${addon.textLimit || 30}" aria-label="${this.t('custom_text_for')} ${this.localeName(addon)}">
                </div>` : ''}
            </div>
        `;}).join('');

        // Delegate events
        container.addEventListener('change', (e) => {
            if (e.target.classList.contains('bh-addon-checkbox')) {
                const row = e.target.closest('.bh-addon-row');
                const handle = row.dataset.handle;
                const isChecked = e.target.checked;
                const textInput = row.querySelector('.bh-addon-text-input');

                if (isChecked) {
                    this.state.addons[handle] = textInput ? (textInput.value || '') : true;
                    if (textInput) textInput.closest('.bh-addon-input-wrapper').classList.remove('bh-is-hidden');
                } else {
                    delete this.state.addons[handle];
                    if (textInput) {
                        textInput.closest('.bh-addon-input-wrapper').classList.add('bh-is-hidden');
                        textInput.value = '';
                    }
                }

                // Dark Colors add-on: toggle dark swatch visibility
                if (handle === 'dark-colors') {
                    this.toggleDarkSwatches(isChecked);
                }

                this.calculateTotal();
            }
        });

        // Text input listener for real-time updates
        container.addEventListener('input', (e) => {
            if (e.target.classList.contains('bh-addon-text-input')) {
                const row = e.target.closest('.bh-addon-row');
                const handle = row.dataset.handle;
                this.state.addons[handle] = e.target.value;
            }
        });

        console.log('Bakehunters: Rendered', this.config.addons.length, 'add-ons');
    }

    updateTimeSlots() {
        if (!this.dom.timeSlotSelect) return;

        // Pickup (and "no method chosen yet") has no delivery window — the shop's opening
        // hours for the chosen day apply instead, rendered by updatePickupHours(). The
        // <select> is only visually hidden, and a hidden-but-enabled <select> still submits
        // its first option: that is how pickup order #1003 shipped "Time Slot: 17:00-19:00".
        // Empty it AND disable it (disabled fields are excluded from FormData outright), so
        // a future re-seed cannot reintroduce the bug.
        if (this.state.deliveryMethod !== 'Delivery') {
            this.dom.timeSlotSelect.innerHTML = '';
            this.dom.timeSlotSelect.disabled = true;
            return;
        }
        this.dom.timeSlotSelect.disabled = false;

        const zoneHandle = this.state.deliveryZone;
        const zoneData = zoneHandle ? this.config.zones.find(z => z.handle === zoneHandle) : null;
        const zoneType = zoneData ? zoneData.type : 'early';

        // Late Zone Warning Element
        let warningEl = document.getElementById('ZoneWarningMsg');
        if (!warningEl && this.dom.deliveryZoneWrapper) {
            warningEl = document.createElement('div');
            warningEl.id = 'ZoneWarningMsg';
            warningEl.style.color = '#e67e22'; // Orange/Warning color
            warningEl.style.fontSize = '0.9em';
            warningEl.style.marginTop = '5px';
            warningEl.style.fontStyle = 'italic';
            warningEl.style.display = 'none';
            this.dom.deliveryZoneWrapper.appendChild(warningEl);
        }

        // Scope Document Section 9.2:
        // Early Zone: 17:00-19:00, 19:00-21:00
        // Late Zone: 20:00-22:00

        if (zoneType === 'late') {
            this.dom.timeSlotSelect.innerHTML = `<option value="20:00-22:00">20:00 - 22:00</option>`;
            if (warningEl) {
                warningEl.textContent = this.t('late_slot_note');
                warningEl.style.display = 'block';
            }
        } else {
            // Early Zone Slots
            this.dom.timeSlotSelect.innerHTML = `
                <option value="17:00-19:00">17:00 - 19:00</option>
                <option value="19:00-21:00">19:00 - 21:00</option>
            `;
            if (warningEl) warningEl.style.display = 'none';
        }
    }

    calculateTotal() {
        console.log('Bakehunters: Calculating total price...');
        let total = 0;

        // Base Price
        if (this.state.basePrice) {
            total += this.state.basePrice;
        }

        // Determine Size Tier (Standard vs Large)
        const isLarge = this.isLargeTier();

        console.log(`  - Size: ${this.state.sizeHandle}, Tier: ${isLarge ? 'Large' : 'Standard'}`);

        // Fillings Logic (Tiered)
        this.state.fillings.forEach(fh => {
            const f = this.config.fillings.find(fil => fil.handle === fh);
            if (f && f.isPremium) {
                let fee = 0;
                if (isLarge && f.premiumFeeLarge) {
                    fee = parseFloat(f.premiumFeeLarge.amount || f.premiumFeeLarge || 0);
                    console.log(`    - Premium Filling (Large): ${f.nameEN} (+${fee} lv)`);
                } else if (f.premiumFee) {
                    fee = parseFloat(f.premiumFee.amount || f.premiumFee || 0);
                    console.log(`    - Premium Filling (Standard): ${f.nameEN} (+${fee} lv)`);
                }
                total += fee;
            }
        });

        // Color Surcharges (Dark) — REMOVED
        // Dark color fee is now handled via the "dark-colors" add-on (€3 standard / €5 large)
        // No separate surcharge calculation needed

        // Add-ons (Tiered)
        if (this.state.addons && Object.keys(this.state.addons).length > 0) {
            Object.keys(this.state.addons).forEach(handle => {
                const addon = this.config.addons.find(a => a.handle === handle);
                if (addon) {
                    const price = isLarge ? addon.priceLarge : addon.priceStandard;
                    total += parseFloat(price || 0);
                }
            });
        }

        // ALWAYS update displayed add-on prices in UI (even if none selected)
        // This ensures prices visually change when switching between Bento (€3) and L/XL/XXL (€5)
        const addOnWrapper = document.getElementById('AddonsContainer');
        if (addOnWrapper) {
            const rows = addOnWrapper.querySelectorAll('.bh-addon-row');
            rows.forEach(row => {
                const handle = row.dataset.handle;
                const addon = this.config.addons.find(a => a.handle === handle);
                if (addon) {
                    const priceSpan = row.querySelector('.addon-price-dynamic');
                    if (priceSpan) {
                        const p = isLarge ? addon.priceLarge : addon.priceStandard;
                        priceSpan.textContent = p.toFixed(2);
                    }
                }
            });
        }

        // Calculate per-unit price (base + fillings + addons + colors)
        let perUnitTotal = total;

        // Get quantity
        const qty = parseInt(this.dom.qtyInput?.value) || 1;

        // Multiply per-unit costs by quantity
        let grandTotal = perUnitTotal * qty;

        // Add per-ORDER fees (not multiplied by quantity)
        // Delivery Fee (per order)
        if (this.state.deliveryZone && !this._freeDelivery()) {
            const z = this.config.zones.find(zn => zn.handle === this.state.deliveryZone);
            if (z && z.fee) {
                grandTotal += parseFloat(z.fee.amount || z.fee || 0);
            }
        }

        // Rush Fee (per order)
        if (this.state.rushFee > 0) {
            grandTotal += this.state.rushFee;
        }

        this.state.totalPrice = grandTotal;
        const formattedPrice = grandTotal.toFixed(2) + " €";

        console.log('Bakehunters: Total calculated:', formattedPrice, '(Qty:', qty, ')');

        // Until a size is chosen there is no real total yet: show a "from {cheapest base}"
        // teaser (e.g. "От 30.00 €") instead of "0.00 €" so the displayed price is never
        // misleading. Once a size is selected, show the exact live total.
        const displayPrice = this.state.sizeHandle ? formattedPrice : this.fromPriceLabel();

        if (this.dom.priceDisplay) this.dom.priceDisplay.textContent = displayPrice;
        if (this.dom.mainPriceDisplay) this.dom.mainPriceDisplay.textContent = displayPrice;
    }

    // Cheapest base price across all sizes, with the localized "from" prefix (e.g. "От 30.00 €").
    // Shown on the price displays before the customer picks a size.
    fromPriceLabel() {
        const prices = (this.config.sizes || [])
            .map(s => {
                const bp = s.basePrice;
                return parseFloat(bp && bp.amount != null ? bp.amount : bp);
            })
            .filter(n => !isNaN(n) && n > 0);
        if (!prices.length) return "0.00 €";
        const min = Math.min(...prices).toFixed(2) + " €";
        return this.t('from_price').replace('{price}', min);
    }
    parseUrlParameters() {
        const params = new URLSearchParams(window.location.search);
        console.log('Bakehunters: Parsing URL Parameters:', params.toString());

        if (params.has('size')) this.state.sizeHandle = params.get('size');
        if (params.has('shape')) this.state.shape = params.get('shape');
        if (params.has('sponge')) this.state.sponge = params.get('sponge'); // Ensure casing matches
        if (params.has('color')) this.state.colorHandle = params.get('color');
        if (params.has('message')) {
            this.state.message = params.get('message');
            if (this.dom.cakeMessage) this.dom.cakeMessage.value = this.state.message;
        }

        // Fillings (comma separated in URL)
        if (params.has('fillings')) {
            const fillingHandles = params.get('fillings').split(',');
            this.state.fillings = fillingHandles.filter(h => h.trim() !== '').slice(0, 2);
        }

        // Check for pre-fill flag implies we might need to update UI immediately after render
    }
}

// Initialize on load (works even if DOMContentLoaded already fired)
function initBakehunters() {
    console.log('Bakehunters: Initializing configurator...');

    if (window.bhApp) {
        console.log('Bakehunters: Already initialized');
        return;
    }

    try {
        window.bhApp = new BakehuntersConfigurator();
        console.log('Bakehunters: Initialization complete!');
    } catch (error) {
        console.error('Bakehunters: Initialization failed!', error);
    }
}

// Try to initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBakehunters);
} else {
    // DOM already loaded
    initBakehunters();
}
